import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import type { CurriculumItem } from '@/data/classes';
import { getScenarioBasicInfo } from '@/services/scenarioService';
import { getAdminClassUserPlayRecords, getAclsAllParticipantsStat } from '@/services/classLabPlatformService';
import { getTriageResult } from '@/services/scenarioService';
import {
	inferScenarioPracticeFamily,
	fetchScenarioPracticePayload,
	getScenarioPracticePlatformGetUrl,
} from '@/utils/scenarioPlatformPracticePaths';
import { buildPlatformGetUrl } from '@/services/platformApiClient';
import { buildRelativeApiUrl, displayBffApiUrl, logSimulationResultsApiSession } from '@/config/studyResultDevConsoleLog';
import type { ChecklistItem, ResultStat } from '@/types/admin';
import type { ScenarioPlayRecordItemDto } from '@/types/api/scenario';
import type { GetClassLabPlayRecordsResponseDto, ClassLabPlayRecordItemDto } from '@/types/api/class';
import { ALL_PARTICIPANTS_ID } from '@/pages/class/components/ParticipantSidebar';
import { apiPreferredKoEn } from '@/lib/i18nLocale';
import {
	getStudyResultPlatformAccessToken,
	getStudyResultPlatformPathUserIdOverride,
} from '@/config/studyResultPlatformAccess';
import { isPlatformReloadOnlyPayload } from '@/utils/scenarioDetailResolver';
import { formatLocaleDateTime } from '@/lib/localeFormat';
import { normalizeTriageResult, mapDemoPatientToTriageDto } from '@/utils/triageResultMapper';
import type { TriageEvaluationResultDto, TriageResultsParams } from '@/types/api/scenarioResult';
import { getPpeMappingByKey } from '@/data/ppeMappingData';

/**
 * 시나리오 플레이기록·플랫폼 실습 URL용 사용자 식별자.
 * MongoDB ObjectId(24자 hex)는 문자열 그대로, 순수 숫자 문자열은 number로 보냄.
 */
function resolveUserIdForScenarioApis(raw: string | undefined): number | string | undefined {
	if (!raw || raw === ALL_PARTICIPANTS_ID) return undefined;
	const t = raw.trim();
	if (!t) return undefined;
	if (/^[a-f0-9]{24}$/i.test(t)) return t;
	if (/^\d+$/.test(t)) {
		const n = Number(t);
		return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
	}
	return t;
}

function formatPlayedAt(iso: string | undefined): string {
	if (!iso) return '';
	const formatted = formatLocaleDateTime(iso);
	return formatted === '-' ? iso : formatted;
}

/** Learn Skills `play_time` 등 밀리초 → 표시 문자열 */
function formatDurationFromMs(ms: number): string {
	const totalSec = Math.max(0, Math.floor(ms / 1000));
	const m = Math.floor(totalSec / 60);
	const s = totalSec % 60;
	if (m <= 0) return `${s}초`;
	if (s <= 0) return `${m}분`;
	return `${m}분 ${s}초`;
}

/** 플랫폼/스튜디오 응답에서 체크리스트 형태 배열 추출 (가능한 경우만) */
function extractChecklistFromUnknown(data: unknown): ChecklistItem[] {
	if (!data || typeof data !== 'object') return [];
	const root = data as Record<string, unknown>;
	
	// PPE: API 응답이 { data: {...} } 구조일 수 있음
	const ppeData = (root as any)?.data ?? root;
	const ppeResults = ppeData.results as Array<Record<string, unknown>> | undefined;
	if (Array.isArray(ppeResults) && ppeResults.length > 0 && ppeData.content_code === 'ppe') {
		const items: ChecklistItem[] = [];
		ppeResults.forEach((item, idx) => {
			const isCorrect = item.result === 1;
			const localizeKey = String(item.step ?? item.localizeKey ?? '-');
			const score = typeof item.score === 'number' ? item.score : 0;
			
			// 키값으로 평가항목·내용 매핑
			const mapping = getPpeMappingByKey(localizeKey);

		items.push({
			id: `ppe-${idx}`,
			order: typeof item.index === 'number' ? item.index : idx + 1,
			rowScoreUnspecified: false,
			evaluationType: 'performance',
			eventName: mapping?.evaluationItem ?? localizeKey,
			taskType: 'To-do',
			taskName: mapping?.evaluationItem ?? '',
			description: mapping?.content ?? '',
			score: score,
			feedback: '',
			status: isCorrect ? 'success' : 'fail',
		});
		});
		if (items.length > 0) return items;
	}
	
	// vr_md (고체온 등): feedback.assessment_table
	const feedback = root.feedback as Record<string, unknown> | undefined;
	const assessmentTable = feedback?.assessment_table as Array<Record<string, unknown>> | undefined;
	if (Array.isArray(assessmentTable) && assessmentTable.length > 0) {
		const items: ChecklistItem[] = [];
		assessmentTable.forEach((item, idx) => {
			const pass = item.pass === true;
			const title = String(item.title ?? '-');
			const description = String(item.description ?? '');
			items.push({
				id: `assessment-${idx}`,
				order: idx + 1,
				rowScoreUnspecified: true,
				evaluationType: 'performance',
				eventName: title,
				taskType: 'To-do',
				taskName: title,
				description: description,
				score: 0,
				feedback: description,
				status: pass ? 'success' : 'fail',
			});
		});
		if (items.length > 0) return items;
	}
	
	// 기존 API의 다양한 필드명 시도 — Learn Skills `scenario-ls` 는 `feedback[]`
	const keys = [
		'feedback',
		'checklist', 'items', 'evaluations', 'taskList', 'rows', 'data', 'list',
		'check_list', 'checkList', 'check_list_feedback', 'checkListFeedback',
		'evaluation_items', 'evaluationItems', 'tasks', 'procedures'
	];
	
	for (const k of keys) {
		const v = root[k];
		if (!Array.isArray(v) || v.length === 0) continue;
		const first = v[0];
		if (first == null || typeof first !== 'object') continue;
		const mapped = v.map((row: Record<string, unknown>, i: number) => mapLooseRowToChecklist(row, i));
		// 유효한 항목이 있으면 반환
		if (mapped.some(item => item.taskName !== '-' || item.eventName !== '-')) {
			return mapped;
		}
	}
	
	// 중첩된 evaluation 객체 안에서도 시도
	const evaluation = root.evaluation as Record<string, unknown> | undefined;
	if (evaluation) {
		// motor_power (근력사정): evaluation.patient_feedbacks
		const patientFeedbacks = evaluation.patient_feedbacks as Array<Record<string, unknown>> | undefined;
		if (Array.isArray(patientFeedbacks) && patientFeedbacks.length > 0) {
			const items: ChecklistItem[] = [];
			const motorPowerParts = ['left_arm', 'left_leg', 'right_arm', 'right_leg'];
			const motorPowerLabels = ['왼팔', '왼다리', '오른팔', '오른다리'];
			
			patientFeedbacks.forEach((patient, patientIdx) => {
				const patientName = String(patient.name ?? `환자 ${patientIdx + 1}`);
				const goodMotor = String(patient.good_motor_power ?? '');
				const selectedMotor = String(patient.selected_motor_power ?? '');
				const comments = patient.comments as string[] | undefined;
				
				if (!goodMotor || !selectedMotor) return;
				
				const goodParts = goodMotor.split('/');
				const selectedParts = selectedMotor.split('/');
				
				motorPowerParts.forEach((part, partIdx) => {
					const isCorrect = goodParts[partIdx] === selectedParts[partIdx];
					const feedbackText = Array.isArray(comments) && comments[partIdx] ? String(comments[partIdx]) : '';
					
					items.push({
						id: `motor-${patientIdx}-${partIdx}`,
						order: items.length + 1,
						rowScoreUnspecified: true,
						evaluationType: 'performance',
						eventName: patientName,
						taskType: 'To-do',
						taskName: motorPowerLabels[partIdx],
						description: '',
						score: 0,
						feedback: feedbackText,
						status: isCorrect ? 'success' : 'fail',
					});
				});
			});
			if (items.length > 0) return items;
		}
		
		// pneumonia 등: evaluation.check_list_feedback + check_list
		const checkListFeedback = evaluation.check_list_feedback as Array<Record<string, unknown>> | undefined;
		const checkList = evaluation.check_list as number[][] | undefined;
		if (Array.isArray(checkListFeedback) && checkListFeedback.length > 0) {
			const items: ChecklistItem[] = [];
			let globalIndex = 0;
			checkListFeedback.forEach((category, catIdx) => {
				const categoryTitle = String(category.title ?? '');
				const categoryItems = category.items as string[] | undefined;
				if (!Array.isArray(categoryItems)) return;
				
				categoryItems.forEach((itemText, itemIdx) => {
					const checkVal = checkList?.[catIdx]?.[itemIdx];
					const pass = checkVal === 1;
					const fail = checkVal === 0;
					items.push({
						id: `checklist-${catIdx}-${itemIdx}`,
						order: globalIndex + 1,
						rowScoreUnspecified: true,
						evaluationType: 'performance',
						eventName: categoryTitle,
						taskType: 'To-do',
						taskName: String(itemText),
						description: '',
						score: 0,
						feedback: '',
						status: pass ? 'success' : fail ? 'fail' : 'partial',
					});
					globalIndex++;
				});
			});
			if (items.length > 0) return items;
		}
		
		for (const k of keys) {
			const v = evaluation[k];
			if (!Array.isArray(v) || v.length === 0) continue;
			const first = v[0];
			if (first == null || typeof first !== 'object') continue;
			const mapped = v.map((row: Record<string, unknown>, i: number) => mapLooseRowToChecklist(row, i));
			if (mapped.some(item => item.taskName !== '-' || item.eventName !== '-')) {
				return mapped;
			}
		}
	}
	
	return [];
}

function mapLooseRowToChecklist(row: Record<string, unknown>, i: number): ChecklistItem {
	const lsFeedbackShape =
		typeof row.pass === 'boolean' &&
		typeof row.acted === 'boolean' &&
		(row.title != null || row.category != null);
	const explicitScoreRaw = row.score ?? row.point ?? row.points ?? row.score_value;
	const hasExplicitItemScore =
		typeof explicitScoreRaw === 'number' && Number.isFinite(explicitScoreRaw);
	// 점수 추출 (다양한 필드명 시도) — LS `feedback[]` 항목에는 점수 필드가 없고 `value`는 다른 의미일 수 있음
	const score = Number(
		hasExplicitItemScore
			? explicitScoreRaw
			: lsFeedbackShape
				? 0
				: (row.score ?? row.point ?? row.points ?? row.value ?? row.score_value ?? 0),
	);
	const rowScoreUnspecified = lsFeedbackShape && !hasExplicitItemScore;
	
	// 성공/실패 상태 판단
	const pass =
		row.status === 'success' ||
		row.status === 'pass' ||
		row.status === 'correct' ||
		row.pass === true ||
		row.isCorrect === true ||
		row.is_correct === true ||
		row.correct === true;
	const fail = 
		row.status === 'fail' ||
		row.status === 'failed' ||
		row.status === 'incorrect' ||
		row.pass === false ||
		row.isCorrect === false ||
		row.is_correct === false ||
		row.correct === false;
	const status = pass ? 'success' : fail ? 'fail' : 'partial';
	
	// 이벤트명 추출
	const eventName = String(
		row.event ?? row.eventName ?? row.event_name ?? row.step ??
		row.stage ?? row.phase ?? row.category ?? row.title ?? '-',
	);
	
	// 태스크명 추출
	const taskName = String(
		row.task ?? row.taskName ?? row.task_name ?? row.title ?? 
		row.name ?? row.label ?? row.description ?? '-'
	);
	
	// 설명 추출
	const description = String(
		row.description ?? row.detail ?? row.details ?? row.content ?? 
		row.text ?? row.comment ?? ''
	);
	
	// 피드백 추출 — description과 별도 필드. LS는 feedback 필드가 없어서 빈 문자열
	const feedback = String(
		row.feedback ??
			row.comment ??
			row.comments ??
			row.message ??
			row.note ??
			row.remark ??
			'',
	);
	
	// 태스크 타입 추출
	let taskType: 'To-do' | 'Decision' | 'Must-not' = 'To-do';
	const taskTypeRaw = String(row.taskType ?? row.task_type ?? row.type ?? '').toLowerCase();
	if (taskTypeRaw.includes('must-not') || taskTypeRaw.includes('mustnot') || taskTypeRaw.includes('forbidden')) {
		taskType = 'Must-not';
	} else if (taskTypeRaw.includes('decision')) {
		taskType = 'Decision';
	}
	// 'must'(단독)는 'To-do'로 처리
	
	return {
		id: String(row.id ?? row.code ?? row.key ?? `api-${i}`),
		order: i + 1,
		...(rowScoreUnspecified ? { rowScoreUnspecified: true as const } : {}),
		evaluationType: String(row.evaluationType ?? row.evaluation_type ?? 'performance').toLowerCase() as 'performance' | 'assessment' | 'equipment',
		eventName,
		taskType,
		taskName,
		description,
		score: Number.isFinite(score) ? score : 0,
		feedback,
		status,
		value: row.value != null ? String(row.value) : row.user_answer != null ? String(row.user_answer) : undefined,
		correctValue: row.correctValue != null ? String(row.correctValue) : row.correct_answer != null ? String(row.correct_answer) : undefined,
	};
}

export interface UseSimulationResultsApiOptions {
	classId: string | undefined;
	sessionItem: CurriculumItem | undefined;
	selectedParticipantId: string;
	isManagerPath: boolean;
	isAllParticipants: boolean;
	lang: string;
	sortCriteria?: string; // 'latest', 'highest', 'lowest', 'all'
	selectedCompletionDate?: string; // 'all' 모드일 때 사용
	startDate?: string; // 날짜 필터 시작
	endDate?: string; // 날짜 필터 종료
	modeValue?: string; // 실습모드 값 (triage 등에서 사용)
}

export function useSimulationResultsApi(opts: UseSimulationResultsApiOptions) {
	const { classId, sessionItem, selectedParticipantId, isManagerPath, isAllParticipants, lang, sortCriteria = 'latest', selectedCompletionDate, startDate, endDate, modeValue } = opts;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [playRecords, setPlayRecords] = useState<ScenarioPlayRecordItemDto[]>([]);
	const [adminLabPayload, setAdminLabPayload] = useState<unknown>(null);
	const [practicePayload, setPracticePayload] = useState<unknown>(null);
	const [scenarioInfo, setScenarioInfo] = useState<Awaited<ReturnType<typeof getScenarioBasicInfo>> | null>(null);
	const [playRecordForScenario, setPlayRecordForScenario] = useState<ClassLabPlayRecordItemDto | null>(null);

	const scenarioId = useMemo(() => {
		if (!sessionItem || sessionItem.type === 'video') return null;
		const id = Number(sessionItem.id);
		return Number.isFinite(id) && id > 0 ? id : null;
	}, [sessionItem]);

	const userIdForApis = useMemo(
		() => resolveUserIdForScenarioApis(selectedParticipantId),
		[selectedParticipantId]
	);

	/**
	 * 세션(·참가자·언어)이 바뀌었을 때, 비동기 `useEffect` 가 돌기 **전** 첫 페인트에 이전 응답 state가 남으면
	 * `SimulationResults`가 `loading===false` 로 **옛** playRecord·practicePayload 를 새 `sessionId` static 과 병합해 잔상이 난다.
	 * 레이아웃 효과에서 곧바로 지우고 로딩으로 두어, 정적 시트만 보이게 한다.
	 */
	useLayoutEffect(() => {
		if (!classId || !sessionItem || sessionItem.type === 'video' || !scenarioId) {
			return;
		}
		setLoading(true);
		setError(null);
		setPlayRecords([]);
		setAdminLabPayload(null);
		setPracticePayload(null);
		setScenarioInfo(null);
		setPlayRecordForScenario(null);
	}, [classId, scenarioId, sessionItem, userIdForApis, isAllParticipants, lang]);

	useEffect(() => {
		if (!classId || !scenarioId || !sessionItem || sessionItem.type === 'video') {
			setPlayRecords([]);
			setAdminLabPayload(null);
			setPracticePayload(null);
			setScenarioInfo(null);
			setPlayRecordForScenario(null);
			setError(null);
			setLoading(false);
			return;
		}

		let cancelled = false;
		const run = async () => {
			const platformToken = getStudyResultPlatformAccessToken();
			const pathUserIdOverride = getStudyResultPlatformPathUserIdOverride();
			const userIdForPlatform = pathUserIdOverride ?? userIdForApis;
			const langCode = apiPreferredKoEn(lang);
			const bffInfoPath = buildRelativeApiUrl(`/scenario/${scenarioId}/info`, { language: langCode || undefined });
			const bffInfoDisplayUrl = displayBffApiUrl(bffInfoPath);

			setLoading(true);
			setError(null);
			try {
				// 1. 시나리오 기본 정보 조회 (contentCode 획득용)
				const info = await getScenarioBasicInfo(scenarioId, langCode, classId).catch(() => null);

				if (cancelled) return;
				setScenarioInfo(info);

				const contentCodeForLab = (info as unknown as Record<string, unknown> | null)?.['key'] as
					| string
					| undefined;
				
				if (!contentCodeForLab) {
					console.warn('[useSimulationResultsApi] contentCode를 찾을 수 없습니다. scenarioId:', scenarioId);
					setError('시나리오 정보를 불러올 수 없습니다.');
					return;
				}

			// 2. 플레이 레코드 리스트 조회 (MongoDB Scenario 플레이 인스턴스 _id 획득용)
			const playRecordsResponseRaw = await getAdminClassUserPlayRecords(
				classId,
				{
					countPerPage: 100,
					pageNum: 1,
					contentCode: contentCodeForLab,
					sortBy: 'ended_at',
					sortOrder: false, // 최신순
					...(modeValue ? { mode: modeValue } : {}),
					// 학생/게스트: 본인 기록만 조회하도록 userId 추가
					...(!isManagerPath && userIdForPlatform != null ? { userId: userIdForPlatform } : {}),
				},
				platformToken
			).catch((err) => {
				console.error('[useSimulationResultsApi] getAdminClassUserPlayRecords 실패:', err);
				return null;
			});

			// reload 응답 체크
			const playRecordsResponse = isPlatformReloadOnlyPayload(playRecordsResponseRaw)
				? null
				: (playRecordsResponseRaw as GetClassLabPlayRecordsResponseDto | null);

			if (cancelled) return;

			// Mongoose 문서 정규화: _doc 안의 데이터를 최상위로 추출
			if (playRecordsResponse?.playRecords) {
				playRecordsResponse.playRecords = playRecordsResponse.playRecords.map((record: any) => {
					// Mongoose 문서 구조인 경우 (_doc가 있는 경우)
					if (record._doc) {
						return {
							...record._doc,
							// 최상위의 user_email, user_name도 포함
							user_email: record.user_email ?? record._doc.user_email,
							user_name: record.user_name ?? record._doc.user_name,
						};
					}
					// 이미 평탄한 구조인 경우
					return record;
				});
			}

			// 3. contentCode와 userId로 매칭된 플레이 레코드 찾기
				let matchedPlayRecord: ClassLabPlayRecordItemDto | null = null;
				if (playRecordsResponse?.playRecords && playRecordsResponse.playRecords.length > 0) {
					const records = playRecordsResponse.playRecords;
					
					console.log('🔍 [useSimulationResultsApi] play-records 응답:', {
						totalRecords: records.length,
						contentCodeForLab,
						firstRecord: records[0] ? {
							content_code: records[0].content_code,
							scenario_id: records[0].scenario_id,
							user_id: records[0].user_id,
							ended_at: records[0].ended_at,
							mode: records[0].mode,
						} : null,
						allContentCodes: records.map(r => r.content_code).filter((v, i, a) => a.indexOf(v) === i),
					});
					
					// contentCode로 필터링
					let filteredRecords = records.filter((r) => r.content_code === contentCodeForLab);
					
					console.log('🔍 [useSimulationResultsApi] contentCode 필터 후:', {
						filteredCount: filteredRecords.length,
						userIdForPlatform,
						isAllParticipants,
					});
					
			// 특정 사용자가 선택되었으면 해당 user_id로 추가 필터링
			if (userIdForPlatform != null && !isAllParticipants) {
				const userIdStr = String(userIdForPlatform);
				filteredRecords = filteredRecords.filter((r) => {
					// user_id나 class_user_id가 일치하는지 확인
					return r.user_id === userIdStr || r.class_user_id === userIdStr;
				});
			}

			// 날짜 필터 적용
			if (startDate || endDate) {
				filteredRecords = filteredRecords.filter((r) => {
					if (!r.ended_at) return false;
					const recordDate = new Date(r.ended_at).toISOString().split('T')[0]; // YYYY-MM-DD
					
					if (startDate && recordDate < startDate) return false;
					if (endDate && recordDate > endDate) return false;
					return true;
				});
			}
				
				// 정렬: sortCriteria에 따라
				if (sortCriteria === 'highest') {
					// 최고 점수: score 내림차순
					filteredRecords.sort((a, b) => {
						const aScore = typeof a.score === 'number' ? a.score : 0;
						const bScore = typeof b.score === 'number' ? b.score : 0;
						return bScore - aScore;
					});
				} else if (sortCriteria === 'lowest') {
					// 최저 점수: score 오름차순
					filteredRecords.sort((a, b) => {
						const aScore = typeof a.score === 'number' ? a.score : 0;
						const bScore = typeof b.score === 'number' ? b.score : 0;
						return aScore - bScore;
					});
				} else if (sortCriteria === 'all') {
					// 전체 기록: 최신순 정렬 후 selectedCompletionDate로 필터링
					filteredRecords.sort((a, b) => {
						const aTime = a.ended_at ?? 0;
						const bTime = b.ended_at ?? 0;
						return bTime - aTime;
					});

					if (selectedCompletionDate) {
						const byCompletion = filteredRecords.filter((r) => {
							const recordDate = formatPlayedAt(r.ended_at ? new Date(r.ended_at).toISOString() : '');
							return recordDate === selectedCompletionDate;
						});
						/** 완료일 목록이 클래스 전체 기준이면 다른 콘텐츠 날짜만 골라 0건이 될 수 있음 → 0건이면 날짜 필터 생략 */
						if (byCompletion.length > 0) {
							filteredRecords = byCompletion;
						}
					}
				} else {
					// 최신 (latest) 또는 기본값: ended_at 내림차순
					filteredRecords.sort((a, b) => {
						const aTime = a.ended_at ?? 0;
						const bTime = b.ended_at ?? 0;
						return bTime - aTime;
					});
				}
				
				// 가장 우선순위가 높은 플레이 레코드 선택
				matchedPlayRecord = filteredRecords[0] ?? null;
				} else {
					// 플레이 레코드 없음
				}

			if (cancelled) return;
			setPlayRecordForScenario(matchedPlayRecord);

			// MongoDB Scenario 플레이 인스턴스 _id
			const mongoScenarioPlayId = matchedPlayRecord?.scenario_id;
			if (!mongoScenarioPlayId) {
				console.warn(
					`[useSimulationResultsApi] 플레이 기록을 찾을 수 없습니다. contentCode: ${contentCodeForLab}, scenarioId: ${scenarioId}`
				);
				// 플레이 기록이 없으면 practicePayload도 null로 두고 종료
				setPracticePayload(null);
				setAdminLabPayload(null);
				setPlayRecords([]);
				setLoading(false);
				return;
			}

			// 정규화된 플레이 레코드 목록을 state에 저장
			const normalizedPlayRecords = playRecordsResponse?.playRecords ?? [];
			const mappedRecords = normalizedPlayRecords.map((record: any) => ({
				scenarioId: typeof scenarioId === 'string' ? parseInt(scenarioId, 10) : scenarioId || 0,
				userId: typeof record.user_id === 'number' ? record.user_id : parseInt(record.user_id) || 0,
				playedAt: record.ended_at ? new Date(record.ended_at).toISOString() : new Date().toISOString(),
				score: typeof record.score === 'number' ? record.score : null,
				userName: record.user_name || undefined,
				classId: typeof classId === 'string' ? parseInt(classId, 10) : classId || undefined,
				contentCode: typeof record.content_code === 'string' ? record.content_code : undefined,
			}));

			// 정렬 적용
			if (sortCriteria === 'highest') {
				mappedRecords.sort((a, b) => {
					const aScore = typeof a.score === 'number' ? a.score : 0;
					const bScore = typeof b.score === 'number' ? b.score : 0;
					return bScore - aScore;
				});
			} else if (sortCriteria === 'lowest') {
				mappedRecords.sort((a, b) => {
					const aScore = typeof a.score === 'number' ? a.score : 0;
					const bScore = typeof b.score === 'number' ? b.score : 0;
					return aScore - bScore;
				});
			} else {
				// 최신순 (playedAt 내림차순) - 'latest', 'all' 모두 포함
				mappedRecords.sort((a, b) => {
					const aTime = new Date(a.playedAt).getTime();
					const bTime = new Date(b.playedAt).getTime();
					return bTime - aTime;
				});
			}

			// 날짜 필터 적용
			let filteredByDate = mappedRecords;
			if (startDate || endDate) {
				filteredByDate = mappedRecords.filter((record) => {
					const recordDate = new Date(record.playedAt);
					const recordDateStr = recordDate.toISOString().split('T')[0]; // YYYY-MM-DD
					
					if (startDate && recordDateStr < startDate) return false;
					if (endDate && recordDateStr > endDate) return false;
					return true;
				});
			}

			setPlayRecords(filteredByDate);

			// 4. 관리자 통계 데이터 (선택적)
				const adminParams = {
					countPerPage: 100,
					pageNum: 1,
					contentCode: contentCodeForLab,
					keyword: String(scenarioId),
				};
				const adminFullUrl = isManagerPath
					? buildPlatformGetUrl(`class/lab/users/play-records/${encodeURIComponent(String(classId))}`, {
							params: adminParams,
						})
					: null;

				let labJson: unknown = null;
				if (isManagerPath && playRecordsResponse) {
					// 이미 가져온 playRecordsResponse를 재사용
					labJson = playRecordsResponse;
				}
				if (cancelled) return;
				setAdminLabPayload(labJson);

		// 5. 플랫폼 실습 상세 데이터 조회 (MongoDB Scenario 플레이 인스턴스 _id 사용)
		const family = inferScenarioPracticeFamily(info, {
			platformContentType: matchedPlayRecord?.content_type,
		});
		
		console.log('🔍 [useSimulationResultsApi] 조건 체크:', {
			family,
			isManagerPath,
			isAllParticipants,
			contentCodeForLab,
			sessionItemId: sessionItem?.id,
			sessionItemType: sessionItem?.type,
		});
		
		// 중증도분류 전체참가자 조회: BFF API 사용
		const isTriageFamilyAllParticipants = family === 'triage' && isManagerPath && isAllParticipants;
		
		// ACLS 전체참가자 조회: BFF API 사용
		const isAclsFamilyAllParticipants = family === 'acls' && isManagerPath && isAllParticipants;
		
		console.log('🎯 [useSimulationResultsApi] isTriageFamilyAllParticipants:', isTriageFamilyAllParticipants);
		console.log('🎯 [useSimulationResultsApi] isAclsFamilyAllParticipants:', isAclsFamilyAllParticipants);
		
		const skipPracticeDetail = isManagerPath && isAllParticipants && !isTriageFamilyAllParticipants && !isAclsFamilyAllParticipants;
		
		let practice: unknown = null;
		let practicePathForLog = '';
		let practiceFullUrlForLog = '';
		
		if (isTriageFamilyAllParticipants) {
			// 중증도분류 전체참가자: BFF API /{lang}/scenario/result/triage/... 호출
			try {
				console.log('🔍 [useSimulationResultsApi] 중증도분류 전체참가자 조회 시작');
				
				// results_params 하드코딩 (재난환자 중증도분류 - 연습 모드 전용)
				// 연습 모드 조건: location='Bus', global_sorting=true, random=false
				const resultsParams: TriageResultsParams = {
					location: 'Bus',
					accident: 'TrafficAccident',
					triage_method: 'START',
					global_sorting: true,  // 연습 모드: true
					random: false,  // 연습 모드: false
					mode: 'triage',
				};
				
				console.log('✅ [useSimulationResultsApi] results_params (하드코딩 - 연습 모드):', resultsParams);
				
				// BFF API 호출
				const triageResponse = await getTriageResult(langCode, classId, resultsParams);
				
				// play-records에서 개인 결과의 정답율 평균 계산
				let avgScoreFromRecords: number | undefined = undefined;
				let avgDurationFromRecords: string | undefined = undefined;
				let winnersFromRecords: any[] = [];
				
				if (playRecordsResponse?.playRecords && playRecordsResponse.playRecords.length > 0) {
					// contentCode로 필터링된 레코드들
					const records = playRecordsResponse.playRecords.filter((r: any) => r.content_code === contentCodeForLab);
					
					if (records.length > 0) {
						// 각 레코드의 score 또는 triage_accuracy 추출
						const scores = records
							.map((r: any) => {
								// score가 0~1 범위면 100 곱하기
								let score = r.score ?? r.triage_accuracy;
								if (typeof score === 'number' && score > 0 && score <= 1) {
									score = score * 100;
								}
								return typeof score === 'number' && !isNaN(score) ? score : null;
							})
							.filter((s: number | null): s is number => s !== null);
						
						if (scores.length > 0) {
							avgScoreFromRecords = Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
						}
						
						// 각 레코드의 소요시간 추출 (밀리초 단위 → 초 단위 변환)
						const durations = records
							.map((r: any) => {
								const duration = r.play_time ?? r.duration ?? r.elapsed_time;
								if (typeof duration === 'number' && !isNaN(duration) && duration >= 0) {
									// 밀리초를 초로 변환 (1000보다 크면 밀리초로 간주)
									return duration > 1000 ? Math.floor(duration / 1000) : duration;
								}
								return null;
							})
							.filter((d: number | null): d is number => d !== null);
						
						if (durations.length > 0) {
							const avgDurationSeconds = Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length);
							// mm:ss 형식으로 변환
							const m = Math.floor(avgDurationSeconds / 60);
							const s = avgDurationSeconds % 60;
							avgDurationFromRecords = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
						}
						
						// 우승자 선발: userId로 중복 제거하고 각 유저의 최고 점수 기준 상위 3명
						const userBestScores = new Map<string, { userId: string; nickname: string; email: string; score: number }>();
						
						records.forEach((r: any) => {
							const userId = r.user_id ?? r.class_user_id;
							const nickname = r.user_name ?? r.nickname ?? r.user_email;
							const email = r.user_email ?? '';
							let score = r.score ?? r.triage_accuracy;
							
							// score가 0~1 범위면 100 곱하기
							if (typeof score === 'number' && score > 0 && score <= 1) {
								score = score * 100;
							}
							
							if (userId && typeof score === 'number' && !isNaN(score)) {
								const existing = userBestScores.get(userId);
								if (!existing || score > existing.score) {
									userBestScores.set(userId, {
										userId,
										nickname,
										email,
										score: Math.round(score),
									});
								}
							}
						});
						
						// 점수 기준 내림차순 정렬 후 상위 3명
						winnersFromRecords = Array.from(userBestScores.values())
							.sort((a, b) => b.score - a.score)
							.slice(0, 3)
							.map((winner) => ({
								nickname: winner.nickname,
								user_email: winner.email,
								score: winner.score,
							}));
						
						console.log('📊 [useSimulationResultsApi] 개인 결과 평균 계산:', {
							recordsCount: records.length,
							scoresCount: scores.length,
							avgScore: avgScoreFromRecords,
							durationsCount: durations.length,
							avgDuration: avgDurationFromRecords,
							uniqueUsers: userBestScores.size,
							winnersCount: winnersFromRecords.length,
						});
					}
				}
				
				console.log('🎯 [useSimulationResultsApi] 중증도분류 BFF API 응답:', {
					demoPatientsCount: triageResponse.demo_patients?.length ?? 0,
					winnersCount: triageResponse.winners?.length ?? 0,
					playStats: triageResponse.play_stats,
					avgScoreFromRecords,
				});
				
				// demo_patients를 TriagePatientDto로 변환
				const langForMapping = lang === 'en' ? 'en' : 'ko';
				const mappedPatients = (triageResponse.demo_patients ?? [])
					.map((p) => mapDemoPatientToTriageDto(p, langForMapping))
					.filter((p): p is NonNullable<ReturnType<typeof mapDemoPatientToTriageDto>> => p != null);
				
				console.log('📊 [useSimulationResultsApi] 매핑된 환자 수:', mappedPatients.length);
				
			// practicePayload 구조로 변환
			practice = {
				averageScore: avgScoreFromRecords,
				patients: mappedPatients,
				demo_patients: triageResponse.demo_patients,
				winners: winnersFromRecords.length > 0 ? winnersFromRecords : triageResponse.winners,
				play_stats: triageResponse.play_stats,
				content_code: contentCodeForLab,
				session_name: '재난환자 중증도분류',
				// stats 객체 추가 (화면 표시용)
				stats: {
					correctRate: avgScoreFromRecords,
					duration: avgDurationFromRecords,
				},
			};
				
				practicePathForLog = `${langCode}/scenario/result/triage/${resultsParams.location}/${resultsParams.accident}/${resultsParams.triage_method}/${resultsParams.global_sorting}/${resultsParams.random}`;
				practiceFullUrlForLog = displayBffApiUrl(buildRelativeApiUrl(practicePathForLog));
				
				console.log('✅ [useSimulationResultsApi] 중증도분류 전체참가자 데이터 준비 완료');
			} catch (err) {
				console.error('[useSimulationResultsApi] 중증도분류 전체참가자 조회 실패:', err);
				setPracticePayload(null);
				return;
			}
		} else if (isAclsFamilyAllParticipants) {
			// ACLS 전체참가자: BFF API /{lang}/scenario-acls/results/lab/stat/{mode} 호출
			try {
				console.log('🔍 [useSimulationResultsApi] ACLS 전체참가자 조회 시작');
				
				// scenarioInfo에서 mode 값 추출
				const modeValue = (scenarioInfo as any)?.modeValue ?? (scenarioInfo as any)?.mode_value ?? 'normal';
				console.log('📌 [useSimulationResultsApi] ACLS mode:', modeValue);
				
				// BFF API 호출
				const aclsStatResponse = await getAclsAllParticipantsStat(
					langCode,
					String(modeValue),
					typeof classId === 'string' ? parseInt(classId, 10) : classId,
					platformToken
				);
				
			console.log('🎯 [useSimulationResultsApi] ACLS BFF API 응답:', aclsStatResponse);
			
			// 응답 구조 확인 - API 응답이 바로 { assessments, lab_stat } 형태
			const responseData = aclsStatResponse as any;
			if (!responseData || !responseData.assessments) {
				console.warn('[useSimulationResultsApi] ACLS BFF API 응답 구조가 올바르지 않습니다.');
				setPracticePayload(null);
				return;
			}
				
				// assessments를 evaluation.group 형태로 변환
				const assessments = responseData.assessments as Array<any>;
				const groups = assessments.map((assessment: any) => {
					// 각 card에 accuracy 필드 추가 (items의 평균 pass_rate)
					const cards = (assessment.cards || []).map((card: any) => {
						const items = card.items || [];
						// items의 평균 pass_rate 계산
						const totalPassRate = items.reduce((sum: number, item: any) => sum + (item.pass_rate || 0), 0);
						const avgPassRate = items.length > 0 ? totalPassRate / items.length : 0;
						
						return {
							...card,
							accuracy: avgPassRate, // 전체 참가자 뷰에서 사용
							items: items, // 기존 items 유지 (key, pass_count, pass_rate 포함)
						};
					});
					
					return {
						key: assessment.key,
						name: assessment.name, // API에서 name이 있으면 사용, 없으면 번역 키로 처리
						pass_count: assessment.pass_count,
						pass_rate: assessment.pass_rate,
						cards: cards,
					};
				});
				
			// practicePayload 구조로 변환
			practice = {
				aclsEvaluation: {
					group: groups,
				},
				scenarioType: 'acls',
				isAllParticipantsView: true,
				lab_stat: responseData.lab_stat,
				content_code: contentCodeForLab,
			};
				
				practicePathForLog = `${langCode}/scenario-acls/results/lab/stat/${modeValue}`;
				practiceFullUrlForLog = displayBffApiUrl(buildRelativeApiUrl(practicePathForLog));
				
				console.log('✅ [useSimulationResultsApi] ACLS 전체참가자 데이터 준비 완료:', {
					groupsCount: groups.length,
					labStat: responseData.lab_stat,
				});
			} catch (err) {
				console.error('[useSimulationResultsApi] ACLS 전체참가자 조회 실패:', err);
				setPracticePayload(null);
				return;
			}
		} else if (!skipPracticeDetail) {
			// 기존 로직: 플랫폼 API 사용
			const practicePathDesc = getScenarioPracticePlatformGetUrl({
				lang,
				classId: String(classId),
				scenarioId: mongoScenarioPlayId,
				contentCode: contentCodeForLab,
				userId: userIdForPlatform,
				isManager: isManagerPath,
				family,
			});
			
			practicePathForLog = practicePathDesc.path;
			practiceFullUrlForLog = practicePathDesc.fullUrl;
			
			practice = await fetchScenarioPracticePayload({
				lang,
				classId,
				scenarioId: mongoScenarioPlayId,
				contentCode: contentCodeForLab,
				userId: userIdForPlatform,
				isManager: isManagerPath,
				family,
				accessToken: platformToken,
			}).catch((err) => {
				console.error('[useSimulationResultsApi] fetchScenarioPracticePayload 실패:', err);
				if (err?.status === 401) {
					console.error('❌ 401 인증 실패: 토큰이 만료되었거나 유효하지 않습니다. 다시 로그인하세요.');
				}
				return null;
			});
		}
			
			console.log('🔍 [useSimulationResultsApi] practice 응답 구조:', {
				hasPractice: !!practice,
				hasData: practice && typeof practice === 'object' && practice !== null && 'data' in practice,
				hasPatients: practice && typeof practice === 'object' && practice !== null && 'patients' in practice,
				hasPatientsInData: practice && typeof practice === 'object' && practice !== null && 'data' in practice && practice.data && typeof practice.data === 'object' && practice.data !== null && 'patients' in practice.data,
				keys: practice && typeof practice === 'object' && practice !== null ? Object.keys(practice).slice(0, 20) : [],
			});
			
			if (cancelled) return;
			setPracticePayload(practice);

			// 개발 환경: 기존 API 응답 구조 확인용 콘솔 로그
			logSimulationResultsApiSession({
				classId: String(classId),
				scenarioId,
				bff: {
					scenarioInfo: {
						method: 'GET',
						url: bffInfoDisplayUrl,
						request: langCode ? { language: langCode } : null,
						response: info,
						...(info == null ? { nullableNote: 'GET /scenario/{id}/info 결과가 null(에러·빈 응답 등으로 폴백)' } : {}),
					},
					playRecords: {
						method: 'GET',
						url: buildPlatformGetUrl(`class/lab/users/play-records/${classId}`, {}),
						request: {
							contentCode: contentCodeForLab,
							countPerPage: 100,
							pageNum: 1,
							sortBy: 'ended_at',
							sortOrder: false,
						},
						response: playRecordsResponse,
						matchedRecord: matchedPlayRecord ?? null,
						mongoScenarioPlayId: mongoScenarioPlayId ?? null,
					},
				},
				platform: {
					adminLab: isManagerPath
						? {
								method: 'GET',
								fullUrl: adminFullUrl ?? '',
								request: adminParams as Record<string, string | number | boolean | undefined>,
								response: labJson,
							}
						: null,
				practice: isTriageFamilyAllParticipants
					? {
							method: 'GET',
							fullUrl: practiceFullUrlForLog,
							path: practicePathForLog,
							skipped: false,
							response: practice,
						}
					: skipPracticeDetail
						? {
								method: 'GET',
								fullUrl: '',
								path: practicePathForLog || '',
								skipped: true,
								skipReason: '관리자+전체 참가자: 플랫폼 ins/data·실습 상세는 생략',
								response: null,
							}
						: {
								method: 'GET',
								fullUrl: practiceFullUrlForLog,
								path: practicePathForLog,
								skipped: false,
								response: practice,
							},
				},
			});
			} catch (e) {
				if (!cancelled) {
					setError(e instanceof Error ? e.message : '결과를 불러오지 못했습니다.');
					setPlayRecords([]);
					setAdminLabPayload(null);
					setPracticePayload(null);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		void run();
		return () => {
			cancelled = true;
		};
	}, [classId, scenarioId, sessionItem, isManagerPath, isAllParticipants, userIdForApis, selectedParticipantId, sortCriteria, selectedCompletionDate, startDate, endDate, lang]);

	const scenarioContentKey =
		(scenarioInfo as { key?: string } | null | undefined)?.key != null &&
		String((scenarioInfo as { key?: string }).key).trim() !== ''
			? String((scenarioInfo as { key?: string }).key).trim()
			: null;

	const completionDatesFromApi = useMemo(() => {
		const set = new Set<string>();
		for (const r of playRecords) {
			// contentCode 필터링
			if (scenarioContentKey) {
				const rc = (r as ScenarioPlayRecordItemDto).contentCode;
				if (rc !== scenarioContentKey) continue;
			}
			// 전체 참가자 보기가 아니면 현재 선택된 참가자로 필터링
			if (userIdForApis != null && !isAllParticipants) {
				const recordUserId = (r as ScenarioPlayRecordItemDto).userId;
				// userIdForApis가 문자열이면 문자열로, 숫자면 숫자로 비교
				if (typeof userIdForApis === 'string') {
					if (String(recordUserId) !== userIdForApis) continue;
				} else {
					if (Number(recordUserId) !== userIdForApis) continue;
				}
			}
			const s = formatPlayedAt(r.playedAt);
			if (s) set.add(s);
		}
		return Array.from(set).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
	}, [playRecords, scenarioContentKey, userIdForApis, isAllParticipants]);

	const checklistItems = useMemo(() => {
		// practicePayload에서 체크리스트 추출 (기존 API 우선)
		const fromPractice = extractChecklistFromUnknown(practicePayload);
		if (fromPractice.length > 0) return fromPractice;
		
		// practicePayload의 latestScenario에서도 시도
		const practiceObj = practicePayload as Record<string, unknown> | null | undefined;
		const latestScenario = practiceObj?.latestScenario as Record<string, unknown> | undefined;
		if (latestScenario) {
			// participants[0].evaluation 경로 시도
			const participants = latestScenario.participants as Array<Record<string, unknown>> | undefined;
			if (participants && participants.length > 0) {
				const evaluation = participants[0]?.evaluation as Record<string, unknown> | undefined;
				if (evaluation) {
					const fromEvaluation = extractChecklistFromUnknown(evaluation);
					if (fromEvaluation.length > 0) return fromEvaluation;
					
					// check_list, check_list_feedback 같은 필드도 시도
					const checkList = evaluation.check_list ?? evaluation.checkList ?? evaluation.checklist;
					if (checkList) {
						const fromCheckList = extractChecklistFromUnknown(checkList);
						if (fromCheckList.length > 0) return fromCheckList;
					}
				}
			}
			// latestScenario 자체에서도 시도
			const fromLatest = extractChecklistFromUnknown(latestScenario);
			if (fromLatest.length > 0) return fromLatest;
		}
		
		// adminLabPayload에서 폴백
		return extractChecklistFromUnknown(adminLabPayload);
	}, [practicePayload, adminLabPayload]);

	const resultMetadata = useMemo(() => {
		const practiceObj = practicePayload as Record<string, unknown> | null | undefined;
		const stats: ResultStat[] = [];

	const isLearnSkillsPayload =
		practiceObj != null &&
		(practiceObj.content_type === 'vr_ls' ||
			(typeof practiceObj.scenario_key === 'string' && /^ls\d{6}$/i.test(String(practiceObj.scenario_key))));

	if (isLearnSkillsPayload) {
			const fb = practiceObj?.feedback;
			const feedbackList = Array.isArray(fb) ? (fb as Record<string, unknown>[]) : [];
			const total = feedbackList.length;
			const correct = feedbackList.filter((r) => r.pass === true).length;
			const pa = practiceObj?.play_accuracy;
			let ratePct: number | null = null;
			if (typeof pa === 'number' && Number.isFinite(pa)) {
				ratePct = pa <= 1 ? Math.round(pa * 100) : Math.round(pa);
			} else if (total > 0) {
				ratePct = Math.round((correct / total) * 100);
			}
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;

			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (total > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correct), sub: `/ ${total}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}

			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
			};
		if (playMs != null && playMs > 0) {
			result.playTimeSeconds = Math.round(playMs / 1000);
		}
		return result;
	}
	
	// PPE: data에 직접 필드가 있음 (API 응답이 { data: {...} } 구조일 수 있음)
	const ppeData = (practiceObj as any)?.data ?? practiceObj;
	const ppeContentCode = ppeData?.content_code;
	
	if (ppeContentCode === 'ppe' && ppeData) {
		// results 배열에서 직접 계산
		const results = ppeData.results as Array<Record<string, unknown>> | undefined;
		const totalCount = Array.isArray(results) ? results.length : (typeof ppeData.total_count === 'number' ? ppeData.total_count : 0);
		const correctCount = Array.isArray(results) ? results.filter(r => r.result === 1).length : (typeof ppeData.correct_count === 'number' ? ppeData.correct_count : 0);
		const totalScore = typeof ppeData.total_score === 'number' ? ppeData.total_score : 0;
		
		// 정답율 계산 (total_score는 이미 비율: 0.7 = 70%)
		const ratePct = Math.round(totalScore * 100);
		
		// 소요시간 계산 (mm:ss 형식)
		const startTime = typeof ppeData.start_time === 'number' ? ppeData.start_time : 0;
		const endTime = typeof ppeData.end_time === 'number' ? ppeData.end_time : 0;
		const playMs = endTime > startTime ? endTime - startTime : 0;
		const totalSec = Math.floor(playMs / 1000);
		const minutes = Math.floor(totalSec / 60);
		const seconds = totalSec % 60;
		const durationStr = playMs > 0 ? `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}` : undefined;
		
		if (ratePct >= 0) {
			stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
		}
		if (totalCount > 0) {
			stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${totalCount}`, color: 'text-gray-800' });
		}
		if (totalScore > 0) {
			// totalScore를 백분율로 표시 (70점 형식)
			stats.push({ label: '총점', value: String(ratePct), sub: '점', color: 'text-blue-600' });
		}
		if (durationStr) {
			stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
		}
		
		const result: Record<string, unknown> = {
			stats,
			tables: [{ title: '수행 평가', type: 'performance' as const }],
			content_code: 'ppe', // PPE 시나리오 식별용
			// evaluationSummary 객체를 직접 제공 (parseEvalSummaryFromMetadata 우회)
			evaluationSummary: {
				score: ratePct,
				scoreSub: '%',
				correctCount: correctCount,
				total: totalCount,
				duration: durationStr,
			},
		};
		if (playMs > 0) {
			result.playTimeSeconds = Math.round(playMs / 1000);
		}
		return result;
	}
	
	// radiation (폭발 사고 / 방사선): participants[0].evaluation.triage_accuracy + patient_feedbacks
	// → triageFromApi에서 처리하도록 통합 (통계만 여기서 계산)
	const scenarioTypeRaw = practiceObj?.scenario_type as string | undefined;
	if (scenarioTypeRaw === 'radiation' && practiceObj) {
		const radiationParticipants = practiceObj.participants as Array<Record<string, unknown>> | undefined;
		const radiationEvaluation = Array.isArray(radiationParticipants) && radiationParticipants.length > 0
			? (radiationParticipants[0]?.evaluation as Record<string, unknown> | undefined)
			: undefined;
		const triageAccuracy = radiationEvaluation?.triage_accuracy ?? radiationEvaluation?.only_triage_accuracy;
		const radiationPatientFeedbacks = radiationEvaluation?.patient_feedbacks as Array<Record<string, unknown>> | undefined;
		
		if (Array.isArray(radiationPatientFeedbacks) && radiationPatientFeedbacks.length > 0 && typeof triageAccuracy === 'number') {
			const casualtiesCount = typeof practiceObj?.casualties_count === 'number' ? practiceObj.casualties_count : radiationPatientFeedbacks.length;
			
			// 정답 수 계산: selected_r_triage === good_r_triage인 환자 수 (방사선 중증도분류 기준)
			const correctCount = radiationPatientFeedbacks.filter((patient) => {
				const goodRTriage = patient.good_r_triage ?? patient.good_triage;
				const selectedRTriage = patient.selected_r_triage ?? patient.selected_triage;
				return goodRTriage != null && selectedRTriage != null && goodRTriage === selectedRTriage;
			}).length;
			
			let ratePct: number | null = null;
			if (Number.isFinite(triageAccuracy)) {
				ratePct = triageAccuracy <= 1 ? Math.round(triageAccuracy * 100) : Math.round(triageAccuracy);
			}
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playSeconds = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const playMs = playSeconds != null ? playSeconds * 1000 : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;
			
			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (casualtiesCount > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${casualtiesCount}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			// 환자 정보 추출 (normalizeTriageResult 사용)
			const enrichedEvaluation = {
				...radiationEvaluation,
				play_time: practiceObj?.play_time,
				duration: practiceObj?.play_time,
				patients: practiceObj?.patients,
			};
			const triageResult = normalizeTriageResult(enrichedEvaluation);
			
			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
				scenarioType: 'radiation',
				contentCode: 'radiation',
				// TriageView에서 사용할 환자 배열
				...(triageResult.patients && triageResult.patients.length > 0 ? { triagePatients: triageResult.patients } : {}),
			};
			if (playSeconds != null && playSeconds > 0) {
				result.playTimeSeconds = playSeconds;
			}
			
			return result;
		}
	}
	
	// COVID-19 (중소병원 대응 실습): evaluation 구조 파싱
	const contentCode = practiceObj?.content_code as string | undefined;
		if (contentCode === 'covid19' && practiceObj) {
			// PPE(개인보호구) 영어명 → 한국어 매핑
			const translatePPE = (englishName: string): string => {
				const ppeMap: Record<string, string> = {
					'HandSanitizer': '손소독제',
					'Gown': '가운',
					'N95Mask': 'N95 마스크',
					'FaceShield': '페이스 실드',
					'OuterGloves': '외과용 장갑',
					'InnerGloves': '내과용 장갑',
					'Shoes': '신발',
					'LevelDProtectionGown': '레벨D 보호복',
					'Goggles': '고글',
					'Mask': '마스크',
					'Gloves': '장갑',
					'HairCap': '헤어캡',
					'SurgicalMask': '수술용 마스크',
				};
				return ppeMap[englishName] || englishName;
			};

			const evaluation = practiceObj.evaluation as Record<string, unknown> | undefined;
			if (evaluation && typeof evaluation === 'object') {
				const totalScore = evaluation.total_score as number || 0;
				const groups = evaluation.group as Array<Record<string, unknown>> | undefined;

				if (Array.isArray(groups)) {
					// 통계 계산
					const totalGroups = groups.length;
					const correctGroups = groups.filter(g => {
						const correctCount = g.correct_count as number || 0;
						const totalCount = g.total_count as number || 0;
						return totalCount > 0 && (correctCount / totalCount) > 0.5;
					}).length;
					
					const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
					const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
					const playMsActual = playMs != null ? playMs * 1000 : null;
					const durationStr = playMsActual != null && playMsActual > 0 
						? (() => {
							const totalSec = Math.floor(playMsActual / 1000);
							const m = Math.floor(totalSec / 60);
							const s = totalSec % 60;
							return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
						})()
						: undefined;
					
					stats.push({ label: '정답율', value: String(totalScore), sub: '%', color: 'text-emerald-600' });
					stats.push({ label: '정답수 / 전체', value: String(correctGroups), sub: `/ ${totalGroups}`, color: 'text-gray-800' });
					if (durationStr) {
						stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
					}
					
					const result: Record<string, unknown> = {
						stats,
						tables: [],
						covid19Evaluation: evaluation,
						scenarioType: 'covid19',
					};
					if (playMs != null && playMs > 0) {
						result.playTimeSeconds = playMs;
					}
					return result;
				}
			}
		}
		
		// ACLS: evaluation.group 구조 파싱 (COVID19와 유사)
		if (contentCode === 'acls' && practiceObj) {
			const evaluation = practiceObj.evaluation as Record<string, unknown> | undefined;
			if (evaluation && typeof evaluation === 'object') {
				const groups = evaluation.group as Array<Record<string, unknown>> | undefined;

				if (Array.isArray(groups)) {
					// 통계 계산: 그룹 안의 개별 항목 단위로 계산
					let totalItems = 0;
					let correctItems = 0;
					
					groups.forEach(g => {
						const correctCount = g.correct_count as number || 0;
						const totalCount = g.total_count as number || 0;
						totalItems += totalCount;
						correctItems += correctCount;
					});
					
					// 정답율 계산: (정답 항목 수 / 전체 항목 수) * 100
					const totalScore = totalItems > 0 
						? Math.round((correctItems / totalItems) * 100)
						: 0;
					
					const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
					const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
					const playMsActual = playMs != null ? playMs * 1000 : null;
					const durationStr = playMsActual != null && playMsActual > 0 
						? (() => {
							const totalSec = Math.floor(playMsActual / 1000);
							const m = Math.floor(totalSec / 60);
							const s = totalSec % 60;
							return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
						})()
						: undefined;
					
					stats.push({ label: '정답율', value: String(totalScore), sub: '%', color: 'text-emerald-600' });
					stats.push({ label: '정답수 / 전체', value: String(correctItems), sub: `/ ${totalItems}`, color: 'text-gray-800' });
					if (durationStr) {
						stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
					}
					
					const result: Record<string, unknown> = {
						stats,
						tables: [],
						aclsEvaluation: evaluation,
						scenarioType: 'acls',
					};
					if (playMs != null && playMs > 0) {
						result.playTimeSeconds = playMs;
					}
					return result;
				}
			}
		}
		
		// IV Injection (정맥주사): comments + patients[0] 데이터 파싱
		const scenarioType = practiceObj?.scenario_type as string | undefined;
		
		// Blood Draw (채혈): blood_draw 시나리오
		if (scenarioType === 'blood_draw' && practiceObj) {
			// 채혈 물품 코드 → 이름 변환
			const getDiagnosticItemName = (type: number | undefined | null): string => {
				if (type == null) return '-';
				const itemMap: Record<number, string> = {
					0: '소독솜',
					1: '토니켓',
					2: '주사기',
					3: '반창고',
					4: '헤어캡',
					5: '덧신',
				};
				return itemMap[type] ?? String(type);
			};
			
			// 검체용기 코드 → 이름 변환
			const getDiagnosticFluidName = (type: number | undefined | null): string => {
				if (type == null) return '-';
				const fluidMap: Record<number, string> = {
					0: 'Plain Tube',
					1: 'Citrate Tube',
					2: 'SST Tube',
					3: 'Heparin Tube',
					4: 'EDTA Tube',
					5: 'Fluorid Tube',
				};
				return fluidMap[type] ?? String(type);
			};
			
			const comments = practiceObj.comments as string[] | undefined;
			const patients = practiceObj.patients as Array<Record<string, unknown>> | undefined;
			const patient = patients && patients.length > 0 ? patients[0] : null;
			
			if (patient && Array.isArray(comments)) {
				// 앵귤러 프로젝트와 동일하게 데이터 순서 재정렬: [1, 2, 0, 3, 4]
				const newOrder = [1, 2, 0, 3, 4];
				const reorderedComments = newOrder.map(index => comments[index] || '');
				const correctPart = patient.correct_part as boolean[] | undefined;
				const reorderedCorrectPart = Array.isArray(correctPart) 
					? newOrder.map(index => correctPart[index] ?? false)
					: [false, false, false, false, false];
				
				// 각 항목별 점수 계산 (correct_part 신뢰)
				const scoreList = [
					reorderedCorrectPart[0] ? 20 : 0, // 0: 물품 선택
					reorderedCorrectPart[1] ? 20 : 0, // 1: 검체용기 선택
					(patient.id_check ? 10 : 0) + (patient.open_ended_question ? 10 : 0), // 2: 대상자 확인
					reorderedCorrectPart[3] ? 20 : 0, // 3: 채혈 소요시간
					reorderedCorrectPart[4] ? 20 : 0, // 4: 검체 담는 순서
				];
				
				// 평가 항목명
				const itemNames = [
					'물품 선택 체크',
					'검체용기 선택',
					'대상자 확인',
					'채혈 소요시간',
					'검체 담는 순서',
				];
				
				// 각 항목별 정답 참고 내용 생성
				const getContentForItem = (idx: number) => {
					switch (idx) {
						case 0: // 물품 선택
							const itemsRequired = patient.items_required as number[] | undefined;
							const itemsSelected = patient.items_selected as number[] | undefined;
							const requiredNames = Array.isArray(itemsRequired) 
								? itemsRequired.map(getDiagnosticItemName).join(', ') 
								: '-';
							const selectedNames = Array.isArray(itemsSelected) 
								? itemsSelected.map(getDiagnosticItemName).join(', ') 
								: '-';
							return [
								{ label: `[필요한 물품] ${requiredNames}`, isError: false },
								{ label: `[선택한 물품] ${selectedNames}`, isError: !reorderedCorrectPart[0] },
							];
						case 1: // 검체용기 선택
							const bloodReq = patient.blood_container_required as number[] | undefined;
							const bloodSel = patient.blood_container_selected as number[] | undefined;
							const bloodReqNames = Array.isArray(bloodReq) 
								? bloodReq.map(getDiagnosticFluidName).join(', ') 
								: '-';
							const bloodSelNames = Array.isArray(bloodSel) 
								? bloodSel.map(getDiagnosticFluidName).join(', ') 
								: '-';
							return [
								{ label: `[필요한 검체용기] ${bloodReqNames}`, isError: false },
								{ label: `[선택한 검체용기] ${bloodSelNames}`, isError: !reorderedCorrectPart[1] },
							];
						case 2: // 대상자 확인
							const idCheck = patient.id_check ? '환자번호 확인' : '환자번호 미확인';
							const openQ = patient.open_ended_question ? '개방형 질문' : '폐쇄형 질문';
							return [
								{ label: '[정답 방법] 환자번호 확인, 개방형 질문', isError: false },
								{ label: `[선택한 방법] ${idCheck}, ${openQ}`, isError: !patient.id_check || !patient.open_ended_question },
							];
						case 3: // 채혈 소요시간
							const timeLimit = '2분 이내';
							const timeResult = reorderedCorrectPart[3] ? '2분 이내' : '2분 초과';
							return [
								{ label: `[제한 시간] ${timeLimit}`, isError: false },
								{ label: `[수행 시간] ${timeResult}`, isError: !reorderedCorrectPart[3] },
							];
						case 4: // 검체 담는 순서
							const orderReq = patient.blood_container_required as number[] | undefined;
							const orderSel = patient.blood_container_order as number[] | undefined;
							const orderReqNames = Array.isArray(orderReq) 
								? orderReq.map(getDiagnosticFluidName).join(' → ') 
								: '-';
							const orderSelNames = Array.isArray(orderSel) 
								? orderSel.map(getDiagnosticFluidName).join(' → ') 
								: '-';
							return [
								{ label: `[정답 순서] ${orderReqNames}`, isError: false },
								{ label: `[선택한 순서] ${orderSelNames}`, isError: !reorderedCorrectPart[4] },
							];
						default:
							return [];
					}
				};
				
				// bloodDrawPerformance 배열 생성
				const bloodDrawPerformance = itemNames.map((name, idx) => ({
					no: idx + 1,
					evaluationItem: name,
					contentItems: getContentForItem(idx),
					result: (scoreList[idx] > 0) ? 'success' : 'fail',
					score: scoreList[idx],
					comment: reorderedComments[idx] || '',
				}));
				
				// 통계 계산 (점수 기준)
				const totalScore = scoreList.reduce((sum, score) => sum + score, 0);
				const correctCount = bloodDrawPerformance.filter(item => item.result === 'success').length;
				const totalCount = bloodDrawPerformance.length; // 5개 항목
				const ratePct = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
				
				const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
				const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
				// Blood draw 데이터는 play_time이 초 단위이므로 1000을 곱해서 밀리초로 변환
				const playMsActual = playMs != null ? playMs * 1000 : null;
				const durationStr = playMsActual != null && playMsActual > 0 
					? (() => {
						const totalSec = Math.floor(playMsActual / 1000);
						const m = Math.floor(totalSec / 60);
						const s = totalSec % 60;
						return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
					})()
					: undefined;
				
				if (ratePct != null) {
					stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
				}
				if (totalCount > 0) {
					stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${totalCount}`, color: 'text-gray-800' });
				}
				if (durationStr) {
					stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
				}
				
				// 환자 정보
				const bloodDrawPatientInfo = {
					name: patient.patient_name as string || '',
					age: patient.age as number || 0,
					gender: patient.gender ? 'M' : 'F',
					registrationNo: patient.id as string || '',
				};
				
				const result: Record<string, unknown> = {
					stats,
					tables: [{ title: '수행 평가', type: 'performance' as const }],
					bloodDrawPerformance,
					bloodDrawPatientInfo,
					bloodDrawPrescribedOrder: patient.prescription as string || '',
					scenarioType: 'blood_draw',
				};
				if (playMs != null && playMs > 0) {
					result.playTimeSeconds = playMs; // play_time은 이미 초 단위
				}
				return result;
			}
		}
		
		if (scenarioType === 'iv_injection' && practiceObj) {
			// 수액 코드 → 이름 변환
			const getMedicationFluidName = (type: number | undefined | null): string => {
				if (type == null) return '-';
				const fluidMap: Record<number, string> = {
					0: '0.9%N/S 1L',
					1: '0.45%N/S 1L',
					2: '5%DW/0.45%NS 1L',
					3: '5%DW 1L',
					4: '5%DW/NaK2 1L',
					5: '5%DS 1L',
					6: '10%DW/0.45%NS 1L',
					7: 'HD 1L',
					8: 'HS 1L',
				};
				return fluidMap[type] ?? String(type);
			};
			
			// 물품 코드 → 이름 변환
			const getMedicationItemName = (type: number | undefined | null): string => {
				if (type == null) return '-';
				const itemMap: Record<number, string> = {
					0: '지혈대',
					1: 'IV 세트',
					2: '카테터',
					3: '소독솜',
					4: '필름 드레싱',
					5: '헤어캡',
					6: '레벨D 신발',
				};
				return itemMap[type] ?? String(type);
			};
			
			const comments = practiceObj.comments as string[] | undefined;
			const patients = practiceObj.patients as Array<Record<string, unknown>> | undefined;
			const patient = patients && patients.length > 0 ? patients[0] : null;
			
			if (patient && Array.isArray(comments)) {
				// 앵귤러 프로젝트와 동일하게 데이터 순서 재정렬: [1, 2, 0, 3, 4]
				const newOrder = [1, 2, 0, 3, 4];
				const reorderedComments = newOrder.map(index => comments[index] || '');
				const correctPart = patient.correct_part as boolean[] | undefined;
				const reorderedCorrectPart = Array.isArray(correctPart) 
					? newOrder.map(index => correctPart[index] ?? false)
					: [false, false, false, false, false];
				
				// 각 항목별 점수 계산
				const hemostasisSuccess = patient.hemostasis_success as boolean[] | undefined;
				const hemostasisPoints = Array.isArray(hemostasisSuccess)
					? hemostasisSuccess.filter(Boolean).length * 7
					: 0;
				const hemostasisScore = hemostasisPoints === 21 ? 20 : hemostasisPoints;
				
				const scoreList = [
					reorderedCorrectPart[0] ? 20 : 0, // 인덱스 0 (원본 1)
					reorderedCorrectPart[1] ? 20 : 0, // 인덱스 1 (원본 2)
					(patient.id_check ? 10 : 0) + (patient.open_ended_question ? 10 : 0), // 인덱스 2 (원본 0)
					hemostasisScore, // 인덱스 3 (원본 3)
					patient.infusion_rate_required === patient.infusion_rate_selected ? 20 : 0, // 인덱스 4 (원본 4)
				];
				
				// 평가 항목명
				const itemNames = [
					'수액 선택 체크',
					'수액 종류',
					'대상자 확인',
					'지혈',
					'수액 주입 속도',
				];
				
				// 각 항목별 정답 참고 내용 생성
				const getContentForItem = (idx: number) => {
					switch (idx) {
						case 0: // 수액 선택 체크 - 물품 리스트
							const itemsRequired = patient.items_required as number[] | undefined;
							const itemsSelected = patient.items_selected as number[] | undefined;
							const requiredNames = Array.isArray(itemsRequired) 
								? itemsRequired.map(getMedicationItemName).join(', ') 
								: '-';
							const selectedNames = Array.isArray(itemsSelected) 
								? itemsSelected.map(getMedicationItemName).join(', ') 
								: '-';
							return [
								{ label: `[필요한 물품] ${requiredNames}`, isError: false },
								{ label: `[선택한 물품] ${selectedNames}`, isError: !reorderedCorrectPart[0] },
							];
						case 1: // 수액 종류
							const fluidReq = getMedicationFluidName(patient.fluid_required as number);
							const fluidSel = getMedicationFluidName(patient.fluid_selected as number);
							return [
								{ label: `[필요한 수액] ${fluidReq}`, isError: false },
								{ label: `[선택한 수액] ${fluidSel}`, isError: patient.fluid_required !== patient.fluid_selected },
							];
						case 2: // 대상자 확인
							const idCheck = patient.id_check ? '환자번호 확인' : '환자번호 미확인';
							const openQ = patient.open_ended_question ? '개방형 질문' : '폐쇄형 질문';
							return [
								{ label: '[정답 방법] 환자번호 확인, 개방형 질문', isError: false },
								{ label: `[선택한 방법] ${idCheck}, ${openQ}`, isError: !patient.id_check || !patient.open_ended_question },
							];
						case 3: // 지혈
							const hemostasisSuccess = patient.hemostasis_success as boolean[] | undefined;
							const hemostasisLabels = ['스타일렛 제거', '지혈대 제거', 'IV 세트 연결'];
							const successItems = Array.isArray(hemostasisSuccess)
								? hemostasisLabels.filter((_, i) => hemostasisSuccess[i]).join(', ')
								: '';
							const hemostasisCount = Array.isArray(hemostasisSuccess) ? hemostasisSuccess.filter(Boolean).length : 0;
							return [
								{ label: '[정답 처치] 스타일렛 제거, 지혈대 제거, IV 세트 연결', isError: false },
								{ label: `[수행한 처치] ${successItems || '처치 없음'} (${hemostasisCount}/3)`, isError: hemostasisCount < 3 },
							];
						case 4: // 수액 주입 속도
							const rateReq = patient.infusion_rate_required === 0 ? 'Full drop' : `${patient.infusion_rate_required} cc/hr`;
							const rateSel = patient.infusion_rate_selected === 0 ? 'Full drop' : `${patient.infusion_rate_selected} cc/hr`;
							return [
								{ label: `[정답 속도] ${rateReq}`, isError: false },
								{ label: `[선택한 속도] ${rateSel}`, isError: patient.infusion_rate_required !== patient.infusion_rate_selected },
							];
						default:
							return [];
					}
				};
				
				// ivPerformance 배열 생성
				const ivPerformance = itemNames.map((name, idx) => ({
					no: idx + 1,
					evaluationItem: name,
					contentItems: getContentForItem(idx),
					result: (scoreList[idx] > 0) ? 'success' : 'fail',
					score: scoreList[idx],
					comment: reorderedComments[idx] || '',
				}));
				
				// 통계 계산 (점수 기준)
				const totalScore = scoreList.reduce((sum, score) => sum + score, 0);
				const correctCount = ivPerformance.filter(item => item.result === 'success').length;
				const totalCount = ivPerformance.length; // 5개 항목
				const ratePct = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
				
				const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
				const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
				// IV 데이터는 play_time이 초 단위이므로 1000을 곱해서 밀리초로 변환
				const playMsActual = playMs != null ? playMs * 1000 : null;
				const durationStr = playMsActual != null && playMsActual > 0 
					? (() => {
						const totalSec = Math.floor(playMsActual / 1000);
						const m = Math.floor(totalSec / 60);
						const s = totalSec % 60;
						return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
					})()
					: undefined;
				
				if (ratePct != null) {
					stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
				}
				if (totalCount > 0) {
					stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${totalCount}`, color: 'text-gray-800' });
				}
				if (durationStr) {
					stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
				}
				
				// 환자 정보
				const ivPatientInfo = {
					name: patient.patient_name as string || '',
					age: patient.age as number || 0,
					gender: patient.gender ? 'M' : 'F',
					registrationNo: patient.id as string || '',
				};
				
				const result: Record<string, unknown> = {
					stats,
					tables: [{ title: '수행 평가', type: 'performance' as const }],
					ivPerformance,
					ivPatientInfo,
					ivPrescribedOrder: patient.prescription as string || '',
					scenarioType: 'iv_injection',
				};
				if (playMs != null && playMs > 0) {
					result.playTimeSeconds = playMs; // play_time은 이미 초 단위
				}
				return result;
			}
		}
		
	// AO (질환별 환자간호): evaluation.feedback 구조
	const aoContentCode = practiceObj?.content_code as string | undefined;
	const evaluation = practiceObj?.evaluation as Record<string, unknown> | undefined;
	if (aoContentCode && /^(pneumonia|bradycardia|antepartumcare|intrapartumcare|evarixbleeding|electrolyteimbalance|acutemyocardialinfarction|gestationaldiabetes)$/.test(aoContentCode)) {
		const feedback = evaluation?.feedback as Record<string, unknown> | undefined;
		if (feedback) {
			// 정답수와 전체 문제수 계산
			let totalCorrect = 0;
			let totalItems = 0;
			Object.values(feedback).forEach((category) => {
				if (Array.isArray(category)) {
					category.forEach((item) => {
						if (typeof item === 'object' && item !== null) {
							totalItems++;
							if (item.pass === true) {
								totalCorrect++;
							}
						}
					});
				}
			});
			
			// 정답율은 항상 계산된 값 사용 (화면 표시와 일치)
			const ratePct = totalItems > 0 ? Math.round((totalCorrect / totalItems) * 100) : null;
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			// mm:ss 형식으로 변환
			let durationStr: string | undefined;
			if (playMs != null && playMs > 0) {
				const totalSec = Math.max(0, Math.floor(playMs / 1000));
				const m = Math.floor(totalSec / 60);
				const s = totalSec % 60;
				durationStr = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
			}
			
			const stats: ResultStat[] = [];
			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (totalItems > 0) {
				stats.push({ label: '정답수 / 전체', value: String(totalCorrect), sub: `/ ${totalItems}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			const result: Record<string, unknown> = {
				stats,
				aoEvaluation: feedback,
				scenarioType: 'ao',
				totalCorrect,
				totalItems,
			};
			if (playMs != null && playMs > 0) {
				result.playTimeSeconds = Math.round(playMs / 1000);
			}
			return result;
		}
	}
	
	// pneumonia 등 VR Act Orders: evaluated_score_avr + check_list_feedback
	const checkListFeedback = evaluation?.check_list_feedback as Array<Record<string, unknown>> | undefined;
	const checkList = evaluation?.check_list as number[][] | undefined;
	if (Array.isArray(checkListFeedback) && checkListFeedback.length > 0) {
			const evaluatedScoreAvr = practiceObj?.evaluated_score_avr ?? practiceObj?.play_accuracy;
			let ratePct: number | null = null;
			if (typeof evaluatedScoreAvr === 'number' && Number.isFinite(evaluatedScoreAvr)) {
				ratePct = evaluatedScoreAvr <= 1 ? Math.round(evaluatedScoreAvr * 100) : Math.round(evaluatedScoreAvr);
			}
			
			// 정답수 계산
			let total = 0;
			let correct = 0;
			if (Array.isArray(checkList)) {
				checkList.forEach((cat) => {
					if (Array.isArray(cat)) {
						cat.forEach((val) => {
							total++;
							if (val === 1) correct++;
						});
					}
				});
			}
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;

			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (total > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correct), sub: `/ ${total}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
			};
			if (playMs != null && playMs > 0) {
				result.playTimeSeconds = Math.round(playMs / 1000);
			}
			return result;
		}
		
		// vr_md (고체온 등): feedback.assessment_info + assessment_table
		const feedback = practiceObj?.feedback as Record<string, unknown> | undefined;
		const assessmentInfo = feedback?.assessment_info as Array<Record<string, unknown>> | undefined;
		const assessmentTable = feedback?.assessment_table as Array<Record<string, unknown>> | undefined;
		if (Array.isArray(assessmentTable) && assessmentTable.length > 0 && Array.isArray(assessmentInfo) && assessmentInfo.length > 0) {
			const info = assessmentInfo[0];
			const correctCount = typeof info.correct_count === 'number' ? info.correct_count : 0;
			const assessmentsCount = typeof info.assessments_count === 'number' ? info.assessments_count : 0;
			
			const playAccuracy = practiceObj?.play_accuracy;
			let ratePct: number | null = null;
			if (typeof playAccuracy === 'number' && Number.isFinite(playAccuracy)) {
				ratePct = playAccuracy <= 1 ? Math.round(playAccuracy * 100) : Math.round(playAccuracy);
			} else if (assessmentsCount > 0) {
				ratePct = Math.round((correctCount / assessmentsCount) * 100);
			}
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playMs = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;
			
			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (assessmentsCount > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${assessmentsCount}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
			};
			if (playMs != null && playMs > 0) {
				result.playTimeSeconds = Math.round(playMs / 1000);
			}
			return result;
		}
		
		// GCS: participants[0].evaluation.gcs_accuracy + gcs_correct
		const participants = practiceObj?.participants as Array<Record<string, unknown>> | undefined;
		const gcsEvaluation = participants?.[0]?.evaluation as Record<string, unknown> | undefined;
		const gcsAccuracy = gcsEvaluation?.gcs_accuracy;
		const gcsCorrect = gcsEvaluation?.gcs_correct;
		const gcsPatientFeedbacks = gcsEvaluation?.patient_feedbacks as Array<Record<string, unknown>> | undefined;
		
		if (Array.isArray(gcsPatientFeedbacks) && gcsPatientFeedbacks.length > 0 && typeof gcsAccuracy === 'number') {
			const casualtiesCount = typeof practiceObj?.casualties_count === 'number' ? practiceObj.casualties_count : 0;
			const correctCount = typeof gcsCorrect === 'number' ? gcsCorrect : 0;
			
			let ratePct: number | null = null;
			if (Number.isFinite(gcsAccuracy)) {
				ratePct = gcsAccuracy <= 1 ? Math.round(gcsAccuracy * 100) : Math.round(gcsAccuracy);
			}
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playSeconds = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const playMs = playSeconds != null ? playSeconds * 1000 : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;
			
			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (casualtiesCount > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${casualtiesCount}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			// gcsPerformance 배열 생성
			const gcsPerformance = gcsPatientFeedbacks.map((patient, idx) => {
				const name = String(patient.name ?? `환자 ${idx + 1}`);
				const gender = patient.gender === true || patient.gender === 'true' || patient.gender_format === true || patient.gender === 'male' || patient.gender === 'm';
				const age = typeof patient.age === 'number' ? patient.age : 0;
				
				const selectedGcs = patient.selected_gcs as Record<string, unknown> | undefined;
				const goodGcs = patient.good_gcs as Record<string, unknown> | undefined;
				const comments = patient.comments as string[] | undefined;
				
				const userEyeOpening = typeof selectedGcs?.eye_opening === 'number' ? selectedGcs.eye_opening : 0;
				const userVerbal = typeof selectedGcs?.verbal === 'number' ? selectedGcs.verbal : 0;
				const userMotor = typeof selectedGcs?.motor === 'number' ? selectedGcs.motor : 0;
				
				const correctEyeOpening = typeof goodGcs?.eye_opening === 'number' ? goodGcs.eye_opening : 0;
				const correctVerbal = typeof goodGcs?.verbal === 'number' ? goodGcs.verbal : 0;
				const correctMotor = typeof goodGcs?.motor === 'number' ? goodGcs.motor : 0;
				
				const userTotal = userEyeOpening + userVerbal + userMotor;
				const correctTotal = correctEyeOpening + correctVerbal + correctMotor;
				
				// 모든 항목이 정답이면 정답, 하나라도 틀리면 오답
				const isAllCorrect = 
					userEyeOpening === correctEyeOpening &&
					userVerbal === correctVerbal &&
					userMotor === correctMotor;
				
				const comment = Array.isArray(comments) ? comments.filter(Boolean).join('\n\n') : '';
				
				return {
					no: idx + 1,
					name,
					gender,
					age,
					user_eye_response: userEyeOpening,
					user_verbal_response: userVerbal,
					user_motor_response: userMotor,
					correct_eye_response: correctEyeOpening,
					correct_verbal_response: correctVerbal,
					correct_motor_response: correctMotor,
					user_total: userTotal,
					correct_total: correctTotal,
					completed_missed: isAllCorrect ? 'completed' : 'missed',
					comment,
				};
			});
			
			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
				scenarioType: practiceObj?.scenario_type ?? 'gcs',
				gcsPerformance,
			};
			if (playSeconds != null && playSeconds > 0) {
				result.playTimeSeconds = playSeconds;
			}
			return result;
		}
		
		// motor_power (근력사정): participants[0].evaluation.motor_power_accuracy + motor_power_correct
		const motorEvaluation = participants?.[0]?.evaluation as Record<string, unknown> | undefined;
		const motorPowerAccuracy = motorEvaluation?.motor_power_accuracy;
		const motorPowerCorrect = motorEvaluation?.motor_power_correct;
		const patientFeedbacks = motorEvaluation?.patient_feedbacks as Array<Record<string, unknown>> | undefined;
		
		if (Array.isArray(patientFeedbacks) && patientFeedbacks.length > 0 && typeof motorPowerAccuracy === 'number') {
			const casualtiesCount = typeof practiceObj?.casualties_count === 'number' ? practiceObj.casualties_count : 0;
			const correctCount = typeof motorPowerCorrect === 'number' ? motorPowerCorrect : 0;
			
			let ratePct: number | null = null;
			if (Number.isFinite(motorPowerAccuracy)) {
				ratePct = motorPowerAccuracy <= 1 ? Math.round(motorPowerAccuracy * 100) : Math.round(motorPowerAccuracy);
			}
			
			const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
			const playSeconds = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
			const playMs = playSeconds != null ? playSeconds * 1000 : null;
			const durationStr = playMs != null && playMs > 0 ? formatDurationFromMs(playMs) : undefined;
			
			if (ratePct != null) {
				stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
			}
			if (casualtiesCount > 0) {
				stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${casualtiesCount}`, color: 'text-gray-800' });
			}
			if (durationStr) {
				stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
			}
			
			// motorPowerPerformance 배열 생성
			const motorPowerPerformance = patientFeedbacks.map((patient, idx) => {
				const name = String(patient.name ?? `환자 ${idx + 1}`);
				const gender = patient.gender === true || patient.gender === 'true' || patient.gender === 'male' || patient.gender === 'm';
				const age = typeof patient.age === 'number' ? patient.age : 0;
				
				const goodMotorPower = String(patient.good_motor_power ?? '');
				const selectedMotorPower = String(patient.selected_motor_power ?? '');
				const comments = patient.comments as string[] | undefined;
				
				const goodParts = goodMotorPower.split('/');
				const selectedParts = selectedMotorPower.split('/');
				
				// API 순서: left_arm / left_leg / right_arm / right_leg
				const leftArm = selectedParts[0] ?? '0';
				const leftLeg = selectedParts[1] ?? '0';
				const rightArm = selectedParts[2] ?? '0';
				const rightLeg = selectedParts[3] ?? '0';
				
				const correctLeftArm = goodParts[0] ?? '0';
				const correctLeftLeg = goodParts[1] ?? '0';
				const correctRightArm = goodParts[2] ?? '0';
				const correctRightLeg = goodParts[3] ?? '0';
				
				// 모든 부위가 정답이면 정답(true), 하나라도 틀리면 오답(false)
				const isAllCorrect = 
					leftArm === correctLeftArm &&
					leftLeg === correctLeftLeg &&
					rightArm === correctRightArm &&
					rightLeg === correctRightLeg;
				
				const comment = Array.isArray(comments) ? comments.filter(Boolean).join('\n\n') : '';
				
				return {
					no: idx + 1,
					name,
					gender,
					age,
					left_arm: leftArm,
					left_leg: leftLeg,
					right_arm: rightArm,
					right_leg: rightLeg,
					correct_left_arm: correctLeftArm,
					correct_left_leg: correctLeftLeg,
					correct_right_arm: correctRightArm,
					correct_right_leg: correctRightLeg,
					completed_missed: isAllCorrect ? 'completed' : 'missed',
					comment,
				};
			});
			
			const result: Record<string, unknown> = {
				stats,
				tables: [{ title: '수행 평가', type: 'performance' as const }],
				scenarioType: practiceObj?.scenario_type ?? 'motor_power',
				motorPowerPerformance,
			};
			if (playSeconds != null && playSeconds > 0) {
				result.playTimeSeconds = playSeconds;
			}
			return result;
		}
		
	// pupil_reaction (동공반응): participants[0].evaluation.pupil_reaction_accuracy + pupil_reaction_correct
	const pupilEvaluation = participants?.[0]?.evaluation as Record<string, unknown> | undefined;
	const pupilReactionAccuracy = pupilEvaluation?.pupil_reaction_accuracy;
	const pupilReactionCorrect = pupilEvaluation?.pupil_reaction_correct;
	const pupilPatientFeedbacks = pupilEvaluation?.patient_feedbacks as Array<Record<string, unknown>> | undefined;
	
	if (Array.isArray(pupilPatientFeedbacks) && pupilPatientFeedbacks.length > 0 && typeof pupilReactionAccuracy === 'number') {
		const casualtiesCount = typeof practiceObj?.casualties_count === 'number' ? practiceObj.casualties_count : 0;
		const correctCount = typeof pupilReactionCorrect === 'number' ? pupilReactionCorrect : 0;
		
		let ratePct: number | null = null;
		if (Number.isFinite(pupilReactionAccuracy)) {
			ratePct = pupilReactionAccuracy <= 1 ? Math.round(pupilReactionAccuracy * 100) : Math.round(pupilReactionAccuracy);
		}
		
		const playTimeRaw = practiceObj?.play_time ?? practiceObj?.playTime;
		const playSeconds = typeof playTimeRaw === 'number' && Number.isFinite(playTimeRaw) ? playTimeRaw : null;
		
		// 소요시간을 MM:SS 형식으로 변환
		let durationStr: string | undefined;
		if (playSeconds != null && playSeconds > 0) {
			const totalSec = Math.max(0, Math.floor(playSeconds));
			const m = Math.floor(totalSec / 60);
			const s = totalSec % 60;
			durationStr = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
		}
		
		if (ratePct != null) {
			stats.push({ label: '정답율', value: String(ratePct), sub: '%', color: 'text-emerald-600' });
		}
		if (casualtiesCount > 0) {
			stats.push({ label: '정답수 / 전체', value: String(correctCount), sub: `/ ${casualtiesCount}`, color: 'text-gray-800' });
		}
		if (durationStr) {
			stats.push({ label: '소요시간', value: durationStr, sub: '', color: 'text-gray-700' });
		}
		
		// pupilReactionPerformance 배열 생성 (각 환자당 좌/우 2개 행)
		const pupilReactionPerformance: Array<Record<string, unknown>> = [];
		pupilPatientFeedbacks.forEach((patient, idx) => {
			const name = String(patient.name ?? `환자 ${idx + 1}`);
			const gender = patient.gender === true || patient.gender === 'true' || patient.gender === 'male' || patient.gender === 'm';
			const age = typeof patient.age === 'number' ? patient.age : 0;
			
			const goodPupil = patient.good_pupil_reaction as Record<string, unknown> | undefined;
			const selectedPupil = patient.selected_pupil_reaction as Record<string, unknown> | undefined;
			const comments = patient.comments as string[] | undefined;
			
			const goodLeft = goodPupil?.eye_left as Record<string, unknown> | undefined;
			const goodRight = goodPupil?.eye_right as Record<string, unknown> | undefined;
			const selectedLeft = selectedPupil?.eye_left as Record<string, unknown> | undefined;
			const selectedRight = selectedPupil?.eye_right as Record<string, unknown> | undefined;
			
			// 크기는 ±1 오차 허용, 모양과 반응은 정확히 일치해야 함
			const leftSizeGood = typeof goodLeft?.size === 'number' ? goodLeft.size : 0;
			const leftSizeSelected = typeof selectedLeft?.size === 'number' ? selectedLeft.size : 0;
			const isSizeCorrectLeft = Math.abs(leftSizeGood - leftSizeSelected) <= 1;
			const isShapeCorrectLeft = goodLeft?.shape === selectedLeft?.shape;
			const isReactionCorrectLeft = goodLeft?.reaction_type === selectedLeft?.reaction_type;
			
			const rightSizeGood = typeof goodRight?.size === 'number' ? goodRight.size : 0;
			const rightSizeSelected = typeof selectedRight?.size === 'number' ? selectedRight.size : 0;
			const isSizeCorrectRight = Math.abs(rightSizeGood - rightSizeSelected) <= 1;
			const isShapeCorrectRight = goodRight?.shape === selectedRight?.shape;
			const isReactionCorrectRight = goodRight?.reaction_type === selectedRight?.reaction_type;
			
			const isAllCorrect = 
				isSizeCorrectLeft && isShapeCorrectLeft && isReactionCorrectLeft &&
				isSizeCorrectRight && isShapeCorrectRight && isReactionCorrectRight;
			
			const comment = Array.isArray(comments) ? comments.filter(Boolean).join('\n\n') : '';
			const resultStr = isAllCorrect ? 'completed' : 'missed';
			
			// 왼쪽 눈 행
			pupilReactionPerformance.push({
				no: idx + 1,
				name,
				gender,
				age,
				position: 'L',
				pupil_size: leftSizeSelected,
				pupil_shape: selectedLeft?.shape ?? 0,
				pupil_reaction: selectedLeft?.reaction_type ?? 0,
				correct_pupil_size: leftSizeGood,
				correct_pupil_shape: goodLeft?.shape ?? 0,
				correct_pupil_reaction: goodLeft?.reaction_type ?? 0,
				result: resultStr,
				comment,
			});
			
			// 오른쪽 눈 행
			pupilReactionPerformance.push({
				no: idx + 1,
				name,
				gender,
				age,
				position: 'R',
				pupil_size: rightSizeSelected,
				pupil_shape: selectedRight?.shape ?? 0,
				pupil_reaction: selectedRight?.reaction_type ?? 0,
				correct_pupil_size: rightSizeGood,
				correct_pupil_shape: goodRight?.shape ?? 0,
				correct_pupil_reaction: goodRight?.reaction_type ?? 0,
				result: resultStr,
				comment,
			});
		});
		
		const result: Record<string, unknown> = {
			stats,
			tables: [{ title: '수행 평가', type: 'performance' as const }],
			scenarioType: practiceObj?.scenario_type ?? 'pupil_reaction',
			pupilReactionPerformance,
		};
		if (playSeconds != null && playSeconds > 0) {
			result.playTimeSeconds = playSeconds;
		}
		return result;
	}
	
	const latestScenario = practiceObj?.latestScenario as Record<string, unknown> | undefined;
		const highestScenario = practiceObj?.highestScenario as Record<string, unknown> | undefined;
		const playStatsFromApi = practiceObj?.play_stats as Record<string, unknown> | undefined;
		const myStats = playStatsFromApi?.my as Record<string, unknown> | undefined;
		const labUsersStats = playStatsFromApi?.labUsers as Record<string, unknown> | undefined;
		
		// 기존 API 응답의 점수 우선 사용
		let latestScore: number | null = null;
		let avgScore: number | null = null;
		let playCount: number = 0;
		
		// 먼저 practicePayload 최상위에서 점수 시도 (새로운 API 구조)
		if (practiceObj) {
			const scoreFromRoot = practiceObj.score ?? practiceObj.total_score ?? practiceObj.totalScore;
			if (typeof scoreFromRoot === 'number' && Number.isFinite(scoreFromRoot)) {
				latestScore = scoreFromRoot;
			}
			// participants[0].evaluation에서도 시도
			const participants = practiceObj.participants as Array<Record<string, unknown>> | undefined;
			if (participants && participants.length > 0) {
				const evaluation = participants[0]?.evaluation as Record<string, unknown> | undefined;
				if (evaluation) {
					// triage_accuracy나 다른 점수 필드
					const triageAccuracy = evaluation.triage_accuracy;
					if (typeof triageAccuracy === 'number' && Number.isFinite(triageAccuracy)) {
						// 0~1 범위면 100을 곱해서 퍼센트로
						if (triageAccuracy <= 1) {
							latestScore = Math.round(triageAccuracy * 100);
						}
					}
					// evaluation.score도 확인
					const evalScore = evaluation.score ?? evaluation.total_score;
					if (typeof evalScore === 'number' && Number.isFinite(evalScore) && latestScore == null) {
						latestScore = evalScore;
					}
				}
			}
		}
		
		// latestScenario에서 점수 추출 (기존 구조 폴백)
		if (latestScenario && latestScore == null) {
			const scoreFromLatest = latestScenario.score ?? latestScenario.total_score ?? latestScenario.totalScore;
			if (typeof scoreFromLatest === 'number' && Number.isFinite(scoreFromLatest)) {
				latestScore = scoreFromLatest;
			}
			// participants[0].evaluation.score 같은 중첩 경로도 시도
			const participants = latestScenario.participants as Array<Record<string, unknown>> | undefined;
			if (participants && participants.length > 0) {
				const evaluation = participants[0]?.evaluation as Record<string, unknown> | undefined;
				const nestedScore = evaluation?.score ?? evaluation?.total_score;
				if (typeof nestedScore === 'number' && Number.isFinite(nestedScore)) {
					latestScore = nestedScore;
				}
			}
		}
		
		// play_stats에서 평균 점수 추출
		if (myStats) {
			const avgFromStats = myStats.avg_score ?? myStats.average_score ?? myStats.avgScore;
			if (typeof avgFromStats === 'number' && Number.isFinite(avgFromStats)) {
				avgScore = Math.round(avgFromStats);
			}
			const playCountFromStats = myStats.play_count ?? myStats.playCount;
			if (typeof playCountFromStats === 'number' && Number.isFinite(playCountFromStats)) {
				playCount = playCountFromStats;
			}
		}
		
		if (latestScore != null) {
			stats.push({ label: '최근 점수', value: String(latestScore), sub: '/ 기록 기준', color: 'text-blue-600' });
		}
		if (avgScore != null && playCount > 1) {
			stats.push({ label: '평균 점수', value: String(avgScore), sub: `(${playCount}건)`, color: 'text-green-600' });
		}
		if (playCount > 0) {
			stats.push({ label: '플레이 기록', value: String(playCount), sub: '건', color: 'text-gray-600' });
		}
		
		const evaluationSummary =
			latestScore != null
				? {
						score: String(latestScore),
						...(typeof latestScore === 'number' && latestScore >= 0 && latestScore <= 100
							? { scoreSub: '%' as const }
							: {}),
					}
				: undefined;
		
		// 기존 API 응답의 다른 메타데이터도 포함
		const result: Record<string, unknown> = {
			stats,
			tables: [{ title: '수행 평가', type: 'performance' as const }],
			...(evaluationSummary != null ? { evaluationSummary } : {}),
			...(practiceObj?.scenario_type ? { scenarioType: practiceObj.scenario_type } : {}),
		};
		
		// play_time 추출 (초 단위)
		if (practiceObj) {
			const playTime = practiceObj.play_time ?? practiceObj.playTime;
			if (typeof playTime === 'number' && Number.isFinite(playTime)) {
				result.playTimeSeconds = playTime;
			}
		}
		
		// 기존 API의 원본 데이터도 포함 (UI에서 필요할 수 있음)
		if (latestScenario) result.latestScenario = latestScenario;
		if (highestScenario) result.highestScenario = highestScenario;
		if (playStatsFromApi) result.playStats = playStatsFromApi;
		if (labUsersStats) result.labUsersStats = labUsersStats;
		
		return result;
	}, [practicePayload]);

	const mustNotViolations = useMemo(
		() => checklistItems.filter((item) => item.taskType === 'Must-not' && item.status === 'fail').length,
		[checklistItems]
	);

	const sessionDisplayName = scenarioInfo?.title?.trim() || sessionItem?.name;

	/**
	 * 플랫폼 실습 GET(예: `.../scenario-covid19/result/{user}/{id}`, triage, ktas, generic/data)
	 * 응답을 `normalizeTriageResult`로 파싱 — `patients`가 있으면 본문·요약에 반영.
	 * 기존 API의 latestScenario.participants[0].evaluation.patient_feedbacks도 확인
	 */
	const triageFromApi: TriageEvaluationResultDto | null = useMemo(() => {
		if (practicePayload == null) return null;

		const practiceObj = practicePayload as Record<string, unknown> | null | undefined;
		if (
			practiceObj?.content_type === 'vr_ls' ||
			(typeof practiceObj?.scenario_key === 'string' && /^ls\d{6}$/i.test(String(practiceObj.scenario_key)))
		) {
			return null;
		}

		// COVID19, IV, Blood Draw, ACLS 시나리오는 별도 파싱 로직 사용 → triage 전용 파싱 제외
		const contentCode = practiceObj?.content_code as string | undefined;
		const scenarioKey = practiceObj?.scenario_key as string | undefined;
		const scenarioType = practiceObj?.scenario_type as string | undefined;
		if (
			contentCode === 'covid19' || 
			contentCode === 'acls' ||
			scenarioKey === 'covid19' || 
			scenarioKey === 'medication' || 
			scenarioKey === 'diagnostic_test'
		) {
			return null;
		}

		// pneumonia 등 nb family: evaluation.check_list_feedback 구조 → triage 전용 파싱 제외
		const evaluation = practiceObj?.evaluation as Record<string, unknown> | undefined;
		const checkListFeedback = evaluation?.check_list_feedback as Array<Record<string, unknown>> | undefined;
		if (Array.isArray(checkListFeedback) && checkListFeedback.length > 0) {
			return null;
		}
	
	// motor_power (근력사정) / pupil_reaction (동공반응) / gcs: participants[0].evaluation 구조 → triage 전용 파싱 제외
	const participants = practiceObj?.participants as Array<Record<string, unknown>> | undefined;
	const motorEvaluation = participants?.[0]?.evaluation as Record<string, unknown> | undefined;
	const motorPowerAccuracy = motorEvaluation?.motor_power_accuracy;
	const pupilReactionAccuracy = motorEvaluation?.pupil_reaction_accuracy;
	const gcsAccuracy = motorEvaluation?.gcs_accuracy;
	const patientFeedbacks = motorEvaluation?.patient_feedbacks as Array<Record<string, unknown>> | undefined;
	
	// Radiation 시나리오는 triage로 처리해야 하므로 제외하지 않음
	const isMotorPupilGcsScenario = scenarioType !== 'radiation' && 
		Array.isArray(patientFeedbacks) && 
		patientFeedbacks.length > 0 && 
		(typeof motorPowerAccuracy === 'number' || typeof pupilReactionAccuracy === 'number' || typeof gcsAccuracy === 'number');
	
	if (isMotorPupilGcsScenario) {
		return null;
	}

	// 먼저 practicePayload.participants[0].evaluation.patient_feedbacks 시도 (새로운 API 구조)
	if (participants && participants.length > 0) {
		const evaluation = participants[0]?.evaluation as Record<string, unknown> | undefined;
		if (evaluation) {
			// play_time은 최상위에 있으므로 evaluation에 추가
			// patients 배열도 최상위에 있으므로 함께 추가 (트리아지 상세 정보 매칭용)
			const enrichedEvaluation = {
				...evaluation,
				play_time: practiceObj?.play_time,
				duration: practiceObj?.play_time,
				patients: practiceObj?.patients,  // 환자 상세 정보 (can, diagnosis, symptoms, aid_history 등)
			};
			
			const fromEvaluation = normalizeTriageResult(enrichedEvaluation);
			if (fromEvaluation.patients && fromEvaluation.patients.length > 0) {
				return fromEvaluation;
			}
		}
	}
	
	// 전체참가자 조회: practicePayload.patients가 이미 매핑된 TriagePatientDto[] 배열인 경우
	if (Array.isArray(practiceObj?.patients) && practiceObj.patients.length > 0) {
		console.log('🔍 [triageFromApi] practiceObj.patients 발견:', {
			patientsCount: practiceObj.patients.length,
			firstPatient: practiceObj.patients[0],
		});
		
		const firstPatient = practiceObj.patients[0];
		// TriagePatientDto 구조인지 확인 (patientName, gender, age, correctAnswer, userAnswer 등)
		if (
			firstPatient &&
			typeof firstPatient === 'object' &&
			'patientName' in firstPatient &&
			'correctAnswer' in firstPatient
		) {
			console.log('✅ [useSimulationResultsApi] 전체참가자 조회 - patients 배열 사용');
			
			// stats 추출 (practiceObj에서 직접 가져오기)
			const statsObj = practiceObj.stats as Record<string, unknown> | undefined;
			const stats = statsObj ? {
				correctRate: statsObj.correctRate as number | undefined,
				score: statsObj.correctRate as number | undefined,
				correctCount: statsObj.correctCount as number | undefined,
				totalCount: statsObj.totalCount as number | undefined,
				duration: statsObj.duration as string | undefined,
			} : undefined;
			
			return {
				patients: practiceObj.patients as any[],
				stats,
			};
		} else {
			console.warn('⚠️ [triageFromApi] patients 배열이 TriagePatientDto 구조가 아님:', {
				hasPatientName: firstPatient && 'patientName' in (firstPatient as any),
				hasCorrectAnswer: firstPatient && 'correctAnswer' in (firstPatient as any),
				keys: firstPatient && typeof firstPatient === 'object' ? Object.keys(firstPatient).slice(0, 10) : [],
			});
		}
	} else {
		console.log('🔍 [triageFromApi] practiceObj 구조 확인:', {
			hasPracticeObj: !!practiceObj,
			hasPatients: practiceObj && 'patients' in practiceObj,
			patientsType: practiceObj?.patients ? typeof practiceObj.patients : 'undefined',
			isArray: Array.isArray(practiceObj?.patients),
			patientsLength: Array.isArray(practiceObj?.patients) ? practiceObj.patients.length : 0,
			keys: practiceObj ? Object.keys(practiceObj).slice(0, 20) : [],
		});
	}
		
		// 기본 정규화 시도
		const normalized = normalizeTriageResult(practicePayload);
		if (normalized.patients && normalized.patients.length > 0) {
			return normalized;
		}
		
		// latestScenario.participants[0].evaluation.patient_feedbacks 경로도 시도 (기존 구조 폴백)
		const latestScenario = practiceObj?.latestScenario as Record<string, unknown> | undefined;
		if (latestScenario) {
			const latestParticipants = latestScenario.participants as Array<Record<string, unknown>> | undefined;
			if (latestParticipants && latestParticipants.length > 0) {
				const evaluation = latestParticipants[0]?.evaluation as Record<string, unknown> | undefined;
				const patientFeedbacks = evaluation?.patient_feedbacks ?? evaluation?.patientFeedbacks;
				if (patientFeedbacks) {
					const fromFeedbacks = normalizeTriageResult(patientFeedbacks);
					if (fromFeedbacks.patients && fromFeedbacks.patients.length > 0) {
						return fromFeedbacks;
					}
				}
			}
		}
		
		// highestScenario에서도 시도
		const highestScenario = practiceObj?.highestScenario as Record<string, unknown> | undefined;
		if (highestScenario) {
			const highestParticipants = highestScenario.participants as Array<Record<string, unknown>> | undefined;
			if (highestParticipants && highestParticipants.length > 0) {
				const evaluation = highestParticipants[0]?.evaluation as Record<string, unknown> | undefined;
				const patientFeedbacks = evaluation?.patient_feedbacks ?? evaluation?.patientFeedbacks;
				if (patientFeedbacks) {
					const fromFeedbacks = normalizeTriageResult(patientFeedbacks);
					if (fromFeedbacks.patients && fromFeedbacks.patients.length > 0) {
						return fromFeedbacks;
					}
				}
			}
		}
		
		if (process.env.NODE_ENV === 'development') {
			console.warn('[useSimulationResultsApi] triageFromApi 생성 실패 - patient_feedbacks를 찾을 수 없음');
		}
		
		return null;
	}, [practicePayload]);

	/** 기존 플랫폼 API 응답에서 winners 배열 추출 */
	const winnersFromApi = useMemo(() => {
		const practiceObj = practicePayload as Record<string, unknown> | null | undefined;
		const adminObj = adminLabPayload as Record<string, unknown> | null | undefined;
		
		// practicePayload에서 winners 찾기
		if (practiceObj?.winners && Array.isArray(practiceObj.winners) && practiceObj.winners.length > 0) {
			return practiceObj.winners as Array<string | { nickname?: string; user_email?: string; score?: number; email?: string }>;
		}
		
		// adminLabPayload에서 winners 찾기
		if (adminObj?.winners && Array.isArray(adminObj.winners) && adminObj.winners.length > 0) {
			return adminObj.winners as Array<string | { nickname?: string; user_email?: string; score?: number; email?: string }>;
		}
		
		// labUsersLatestAvg.winner 같은 중첩된 경로도 확인
		const labAvg = practiceObj?.labUsersLatestAvg as Record<string, unknown> | undefined;
		if (labAvg?.winner && Array.isArray(labAvg.winner) && labAvg.winner.length > 0) {
			return labAvg.winner as Array<string | { nickname?: string; user_email?: string; score?: number; email?: string }>;
		}
		
		return [];
	}, [practicePayload, adminLabPayload]);

	/** 
	 * 중증도분류 연습 모드 여부 확인
	 * - 재난환자 중증도분류: modeValue === '1'이 연습 모드
	 * - 연습 모드일 때만 전체참가자 통계 제공 (demo_patients)
	 */
	const isTriagePracticeMode = useMemo(() => {
		if (!scenarioInfo) return false;
		const info = scenarioInfo as any;
		
		// modeValue 확인
		const modeValue = info.modeValue ?? info.mode_value;
		if (modeValue === '1' || modeValue === 1) {
			console.log('✅ [useSimulationResultsApi] 연습 모드 확인: modeValue =', modeValue);
			return true;
		}
		
		// modeValueList에서도 확인 (첫 번째 항목)
		const modeValueList = info.modeValueList ?? info.mode_value_list;
		if (Array.isArray(modeValueList) && modeValueList.length > 0) {
			const firstMode = modeValueList[0];
			const firstModeValue = typeof firstMode === 'object' ? firstMode.modeValue : firstMode;
			if (firstModeValue === '1' || firstModeValue === 1) {
				console.log('✅ [useSimulationResultsApi] 연습 모드 확인: modeValueList[0] =', firstModeValue);
				return true;
			}
		}
		
		console.log('ℹ️ [useSimulationResultsApi] 연습 모드 아님:', { modeValue, modeValueList });
		return false;
	}, [scenarioInfo]);

	return {
		loading,
		error,
		playRecords,
		adminLabPayload,
		practicePayload,
		scenarioInfo,
		playRecordForScenario,
		sessionDisplayName,
		completionDatesFromApi,
		checklistItems,
		resultMetadata,
		mustNotViolations,
		isVideoSession: sessionItem?.type === 'video',
		triageFromApi,
		winnersFromApi,
		isTriagePracticeMode,
	};
}
